import {Component, inject, OnDestroy, OnInit, Type, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {forkJoin, Observable, of, Subscription} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {CommonModule} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faChevronLeft, faChevronRight} from '@fortawesome/pro-solid-svg-icons';
import {faSpinnerThird} from '@fortawesome/pro-duotone-svg-icons';
import {BicButtonComponent} from '@buildinginformationcloud/bic-ui-components';

import {MyChecksService} from '../my-checks.service';
import {RulesetService} from '../../../services/ruleset.service';
import {Ruleset} from '../../ruleManagement/models/rule-management.models';
import {PageHeaderComponent} from '../../util/design/page-header/page-header.component';
import {StepperStep} from '../shared/my-checks-stepper/my-checks-stepper.component';
import {
  LogDetailRow,
  LogDetailState,
  StepLogComponent,
  truncateDetailValue,
} from '../steps/step-log/step-log.component';
import {StepProcessingComponent} from '../steps/step-processing/step-processing.component';
import {CheckErrorComponent} from '../shared/check-error/check-error.component';
import {
  CombinedSelectionPlan,
  CombinedSelectionResult,
  StepCombinedSelectComponent,
} from '../steps/step-combined-select/step-combined-select.component';
import {StateConfigService} from '../../../services/state/state-config.service';
import {
  CheckDurations,
  CheckProgress,
  RunLogPage,
  StepLogDetail,
  WorkflowExecutionEvent,
  WorkflowService,
  WorkflowSubRunInfo
} from '../../../services/workflow.service';
import {CheckResultControllerService, SimpleCheck} from '../../../api';
import {resolveStepComponent} from '../steps/step-registry';
import {StepCommunicationService} from '../steps/step-communication.service';
import {Manifest} from '../../../api/model/manifest';
import {ModuleManifest} from '../../../api/model/moduleManifest';
import {
  extractReportSelectionFromManifest,
  ReportDisplayConfig,
  resolveReportDisplayConfig
} from '../../../models/report/report-display-config';
import {
  ExecutionStep,
  LogEntry,
  ProgressInfo,
  SelectedFileSummary,
  SelectedRuleSummary
} from '../steps/step-log/step-log.model';

/** Tracks the visible stepper steps derived from the workflow manifest. */
interface ExecutionStepInfo {
  nodeId: string;
  label: string;
  type: 'interactive' | 'module' | 'result';
  interactionType?: string;
  moduleId?: string;
}

/**
 * Executor-Module, die ihre eigentliche Arbeit in Kind-Instanzen ausführen. Deren Modul-Schritte
 * – und damit die kompletten Modul-Logs – stecken nicht in `instance.steps` der Elternprüfung und
 * müssen über /children nachgeladen werden.
 *
 * `multi_check_executor` fehlte hier: bei einem gemischten Lauf zeigte das Ereignisprotokoll
 * deshalb nur die zwei Einträge der interaktiven Schritte, während die IDS-Prüfung selbst
 * dutzende Log-Einträge in der Kind-Instanz liegen hatte.
 */
/**
 * Seitengröße für das Ereignisprotokoll.
 *
 * Ein Prüflauf hat typischerweise einige hundert Schritte; 500 deckt praktisch jeden Lauf in einer
 * Seite ab. Ist mehr vorhanden, sagt die Ansicht das ausdrücklich statt still abzuschneiden.
 */
const RUN_LOG_PAGE_SIZE = 500;

const CHILD_SPAWNING_EXECUTORS = ['hybrid_ruleset_executor', 'multi_check_executor'];

/**
 * Module, deren Schritt die Einzelprüfungen trägt – der eine Schritt, in dem ein Lauf praktisch
 * seine gesamte Zeit verbringt.
 *
 * Diese Liste bestimmt den Bezugspunkt der Fortschrittsanzeige. Sie wird aus dem MANIFEST
 * ausgewertet, also bevor der Lauf startet: Früher wurde der Knoten beim ersten Fortschrittsstand
 * geraten (der Schritt, der da gerade lief), was bei einem verzögerten ersten Stand den falschen
 * Knoten traf und den Balken springen ließ.
 */
const UNIT_CARRYING_MODULES = [
  'ai_check',
  'hoai_check',
  'check_execution',
  ...CHILD_SPAWNING_EXECUTORS,
];

function carriesUnits(moduleId?: string): boolean {
  if (!moduleId) return false;
  // Wie oben: der Hybrid-Executor trägt bei Fan-Out ein Fortschritts-Suffix.
  const normalized = String(moduleId).toLowerCase().split(':')[0];
  return UNIT_CARRYING_MODULES.includes(normalized);
}

@Component({
  selector: 'my-checks-execution',
  standalone: true,
  imports: [
    CommonModule,
    FaIconComponent,
    BicButtonComponent,
    PageHeaderComponent,
    StepCombinedSelectComponent,
    StepLogComponent,
    StepProcessingComponent,
    CheckErrorComponent,
  ],
  providers: [StepCommunicationService],
  templateUrl: './my-checks-execution.component.html',
  styleUrls: ['./my-checks-execution.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {class: 'flex flex-col h-full w-full'},
})
export class MyChecksExecutionComponent implements OnInit, OnDestroy {
  // View state
  currentView: 'init' | 'loading' | 'selection' | 'interactive' | 'failed' = 'init';
  activeInteractionComponent: Type<any> | null = null;
  activeInteractionInputs: Record<string, any> = {};

  currentPrompt: any;
  currentStepInfo: any;
  outputData: any;
  errorMessage = '';
  /** Per-file breakdown for a failed IDS multi-file check, shown in the "Details anzeigen" panel. */
  errorDetails = '';
  loadingMessage = 'Prüfung wird gestartet...';
  projectId = 0;
  checkName = '';

  protected readonly faChevronLeft = faChevronLeft;
  /** Id of an existing report for the viewed instance, if one exists (only set for the log route). */
  reportId?: string;
  protected readonly faSpinnerThird = faSpinnerThird;
  /** Cumulated check duration in ms per rule name, extracted from the check_execution step output. */
  /**
   * Serverseitig aggregierte Prüfdauern des Laufs (je Prüfregel und je Datei).
   *
   * Ersetzt die frühere clientseitige Rechnung aus `instance.steps`: Die konnte für HYBRID und
   * gemischte Läufe strukturell nichts finden, weil die tragenden Schritte dort in Kind- und
   * Enkel-Instanzen liegen, die in `instance.steps` gar nicht auftauchen.
   */
  checkDurations: CheckDurations | null = null;

  /** Zustand des im Protokoll aufgeklappten Eintrags (Details werden erst dann geladen). */
  activeLogDetail: LogDetailState | null = null;

  /** Verhindert überlappende Protokoll-Requests, wenn ein Poll-Tick auf einen langsamen Vorgänger trifft. */
  private runLogInFlight = false;

  // Stepper state – dynamically derived from manifest
  stepperSteps: StepperStep[] = [];
  currentStepIndex = 0;
  completedSteps: number[] = [];
  currentModuleId = '';

  // Step progress tracking for the new loading UI
  progressSteps: ExecutionStep[] = [];
  progressLogs: LogEntry[] = [];
  progressInfo: ProgressInfo = {
    currentStep: 0,
    totalSteps: 0,
    elapsedSeconds: 0,
    percentage: 0,
    checkName: '',
  };
  private executionStartTime?: number;
  private elapsedTimer?: any;
  /**
   * Obergrenze, bis zu der der künstliche Creep hochzählt; darüber übernimmt der echte Fortschritt.
   *
   * Der Creep hat nur eine Aufgabe: die ersten Sekunden überbrücken, in denen es noch kein Signal
   * gibt. Er darf deshalb das Vorbereitungsband ({@link PREP_BAND_MAX}) nicht überschreiten –
   * sonst schöbe er den Balken in den Bereich, der den Einzelprüfungen gehört, und die Zahl
   * darunter ("0 von 100") straft ihn Lügen.
   */
  private static readonly PROGRESS_CREEP_CAP = 10;

  /**
   * Bänder der Fortschrittsanzeige.
   *
   * Der Balken folgt nicht mehr den Workflow-Schritten, sondern der tatsächlichen Arbeit. Ein Lauf
   * verbringt praktisch seine gesamte Zeit in EINEM Schritt (dem Einheiten-Schritt); Vorbereitung
   * und Berichtserstellung sind Sekunden. Also bekommen sie feste, kleine Bänder, und dazwischen
   * ist der Balken eine direkte Funktion von `unitsDone / unitsTotal`.
   *
   * Vorher wurden die Schritte gewichtet gezählt. Das hatte zwei Fehler, die den Balken springen
   * ließen: Der Nenner wuchs erst mit den eintreffenden Ereignissen (fünf gesehene Schritte, vier
   * davon fertig = 90 %, egal wie viel noch kam), und das Gewicht des Einheiten-Schritts hing an
   * dem Knoten, der beim ERSTEN Fortschrittsstand zufällig lief – traf das einen
   * Vorbereitungsschritt, sprang der Balken bei dessen Abschluss um das gesamte Gewicht.
   */
  /** Obergrenze der Vorbereitungsphase (Einheiten-Schritt hat noch nicht begonnen). */
  private static readonly PREP_BAND_MAX = 10;
  /** Obergrenze der Einzelprüfungs-Phase; darüber laufen nur noch Aggregation und Bericht. */
  private static readonly UNIT_BAND_END = 85;
  /** Obergrenze vor dem echten Abschluss; 100 % gibt es ausschließlich bei COMPLETED. */
  private static readonly TAIL_BAND_END = 99;
  /**
   * Obergrenze des Creeps, solange der Einheiten-Schritt zwar läuft, aber noch keinen Plan gemeldet
   * hat.
   *
   * Zwei Fälle teilen sich diese Lücke, und der Wert ist der Kompromiss zwischen ihnen: Beim
   * KI-Lauf dauert sie ein, zwei Sekunden (der Fan-Out schreibt seinen Plan beim Start). Eine reine
   * IDS-Prüfung meldet dagegen NIE Einheiten – dort prüft ein Modulaufruf alle Dateien auf einmal,
   * es gibt keine feinere Einheit. Der Balken darf deshalb nicht am Bandanfang festhängen, aber
   * auch nicht so weit laufen, dass ein spät gemeldeter echter Stand ihn wieder einfriert.
   */
  private static readonly UNIT_BAND_CREEP_CAP = 25;
  /** Poll-Intervall des schlanken /progress-Endpunkts (Einzelprüfungen). */
  private static readonly PROGRESS_POLL_INTERVAL_MS = 3000;
  /**
   * Künstlicher "Creep"-Timer, der die Anzeige in den ersten Sekunden gleichmäßig hochzählt,
   * damit der Balken nicht ruckartig springt (siehe {@link startProgressCreep}).
   */
  private progressCreepTimer?: any;
  /**
   * Monotone Untergrenze für die Fortschrittsanzeige. Verhindert, dass der Ladebalken
   * zurückspringt, wenn SSE-Events und Polling unterschiedliche Zwischenstände liefern.
   */
  private progressFloor = 0;
  /**
   * Poll-Intervall des vollständigen Instanz-Abrufs (Sicherheitsnetz für den Endstatus).
   *
   * Bewusst deutlich seltener als der Fortschritts-Poll: `GET /workflows/instances/{id}` liefert
   * ALLE StepLogs samt `inputData`/`outputData` – bei einem KI-Lauf über hunderte Datei-Regel-Paare
   * mehrere Megabyte. Alle 5 s war das Last auf genau der Datenbank, die parallel die Modul-Responses
   * desselben Laufs verarbeitet. Den Endstatus erkennt jetzt der Fortschritts-Poll und löst dann
   * genau einen vollständigen Abruf aus.
   */
  private static readonly INSTANCE_POLL_INTERVAL_MS = 20000;
  /**
   * Knoten, der die Einzelprüfungen trägt (gesetzt beim ersten Fortschrittsstand). Nur dieser
   * Schritt wird gewichtet; ohne ihn rechnet die Anzeige wie vorher mit gleichen Gewichten.
   */
  private unitStepNodeId?: string;
  private progressPollTimer?: any;
  private progressPollInFlight = false;

  // Track user selections for persisting CheckResult
  private userInputs: Record<string, any> = {};
  private selectedRuleIds: string[] = [];
  private selectedFileIds: string[] = [];
  private checkResultId?: string;
  private executionSteps: ExecutionStepInfo[] = [];
  private moduleRegistry: ModuleManifest[] = [];
  private workflowSlug?: string;
  /** Version des myCheck-Definitions (aus WorkflowDefinition.version) für die Overview-Karte. */
  private checkVersion?: string;
  private definitionCheckType?: string;

  /** Rich rule/file summaries for the loading screen display */
  selectedRulesData: SelectedRuleSummary[] = [];
  selectedFilesData: SelectedFileSummary[] = [];
  protected readonly faChevronRight = faChevronRight;
  /** True sobald die Regel-Zusammenfassung aus dem tatsaechlichen Instanz-State stammt (Log-Ansicht).
   *  Verhindert, dass die Manifest-Vorauswahl die echten Lauf-Regeln ueberschreibt. */
  private rulesLoadedFromInstance = false;

  /** Plan für die kombinierte Vorab-Auswahl (Regeln + Dateien auf einem Screen). */
  selectionPlan: CombinedSelectionPlan | null = null;
  /** Zwischengespeicherte Resume-Payloads, die beim Erreichen der Interaktionsschritte automatisch gesendet werden. */
  private pendingRuleData?: Record<string, unknown>;
  private pendingFileData?: Record<string, unknown>;
  /** Trigger-Daten für den Workflow-Start (vordefinierter Dateimodus: enthält project_files). */
  private pendingTriggerData?: Record<string, unknown>;
  /** Optionaler Berichtsname aus dem Bestätigungs-Popup; `undefined` = Name des myChecks. */
  private pendingReportName?: string;
  /** Anzeige-/Spaltenkonfiguration aus der MyCheck-Berichtsgenerierung. */
  reportDisplayConfig: ReportDisplayConfig | null = null;

  get checkRuleType(): string {
    return this.definitionCheckType ?? 'HYBRID';
  }

  private myChecksService = inject(MyChecksService);
  private rulesetService = inject(RulesetService);
  private flowService = inject(WorkflowService);
  private checkResultService = inject(CheckResultControllerService);
  private stepComm = inject(StepCommunicationService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private stateConfig = inject(StateConfigService);

  get isLogRoute(): boolean {
    return this.route.snapshot.routeConfig?.path === 'log'
      || this.router.url.includes('/my-checks/log');
  }
  private subscription?: Subscription;
  private commSubscription?: Subscription;
  private routeSubscription?: Subscription;
  private instanceId?: string;
  private definitionId?: string;
  private pollTimer?: any;
  /** Verhindert überlappende Polling-Requests, falls eine Backend-Antwort länger als das 5s-Intervall braucht. */
  private pollInFlight = false;
  private savingInProgress = false;
  /** Verhindert doppelte Erstattungs-Requests innerhalb dieser Session (das Backend ist
   *  zusätzlich idempotent). Wird pro Prüflauf zurückgesetzt. */
  private refundRequested = false;
  /** Prevents the queryParams subscription from resetting pending interaction data when
   *  startExecution() adds instanceId to the URL for page-reload support. */
  private skipNextExecutionReset = false;
  ngOnInit() {
    // One-time setup: step communication and module registry
    this.commSubscription = this.stepComm.submitted$.subscribe(data => this.onUserInput(data));
    this.commSubscription.add(this.stepComm.cancelled$.subscribe(() => this.onCancel()));

    this.flowService.listModules().subscribe({
      next: (modules) => this.moduleRegistry = modules,
      error: () => {},
    });

    // Re-initialize whenever query params change (e.g. navigating between pinned checks)
    this.routeSubscription = this.route.queryParams.subscribe(params => {
      // When startExecution() adds instanceId to the URL for reload support, skip the full
      // reset so that pendingRuleData/pendingFileData survive for auto-resume.
      if (this.skipNextExecutionReset) {
        this.skipNextExecutionReset = false;
        return;
      }

      this.subscription?.unsubscribe();
      this.stopPolling();
      this.stopElapsedTimer();
      this.resetExecutionState();

      this.definitionId = params['definitionId'];
      const workflowSlug = params['workflow'];
      const existingInstanceId = params['instanceId'];
      this.projectId = this.stateConfig.getProjectId();

      if (existingInstanceId) {
        this.instanceId = existingInstanceId;
        this.resumeExistingInstance(existingInstanceId);
      } else {
        this.initWorkflow(workflowSlug);
      }
    });
  }

  /**
   * Öffnet den Bericht, der zu dieser (Log-)Ansicht gehört. Ist die Prüfung noch nicht
   * abgeschlossen (`reportId` kann auch für RUNNING/ARCHIVED gesetzt sein, siehe
   * resolveReportIdForInstance), gibt es noch keinen Bericht – dann zurück auf den Ladescreen
   * statt auf eine leere/fehlerhafte Berichtsseite.
   */
  onViewReport() {
    if (!this.reportId) return;
    if (this.progressInfo.status !== 'COMPLETED') {
      this.router.navigate(
        ['projects', this.projectId, 'check-runs', 'execute'],
        {queryParams: {instanceId: this.instanceId}},
      );
      return;
    }
    // `this.reportId` ist bewusst nur die Existenz-Prüfung oben (checkResultId, siehe
    // resolveReportIdForInstance) – als Navigationsziel muss dieselbe ID wie bei navigateToReport()
    // und in der Berichtsliste (reports.component.ts getReportId()) verwendet werden: die
    // Workflow-Instanz-ID. Sonst landet loadReport() über den checkResultId-Fallback im
    // "unbekannter Typ"-Zweig, und die Spalten zeigen die falsche Standardreihenfolge (Dateien vor
    // Prüfregeln) statt der im myCheck definierten Reihenfolge.
    this.router.navigate(
      ['projects', this.projectId, 'reports', this.getReportNavigationId()],
      {queryParams: {type: this.checkRuleType}},
    );
  }

  /** ID für die Berichts-Route – analog zu getReportId() in reports.component.ts. */
  private getReportNavigationId(): string {
    if (this.checkRuleType === 'IDS') return this.reportId!;
    return this.instanceId ?? this.reportId!;
  }

  /**
   * Leitet nach Abschluss auf die geteilte Berichtsseite um (analog zur Reports-Liste), damit
   * „nach Abschluss" und „aus der Liste geöffnet" denselben Renderer/URL nutzen.
   * Nur für den Nicht-Log-Pfad; die Log-Ansicht bleibt ein reines Protokoll.
   * KI/HYBRID laden aus der Workflow-Instanz; IDS greift auf den Instanz-Fallback der Berichtsseite zurück.
   */
  private navigateToReport() {
    if (this.isLogRoute || !this.instanceId) return;
    this.router.navigate(
      ['projects', this.projectId, 'reports', this.instanceId],
      {queryParams: {type: this.checkRuleType}},
    );
  }

  onUserInput(data: any) {
    if (!this.instanceId) return;
    this.currentView = 'loading';
    this.loadingMessage = 'Eingabe wird verarbeitet...';

    // Track selections generically
    if (data.rule_id) this.selectedRuleIds.push(data.rule_id);
    if (Array.isArray(data.rule_ids)) {
      data.rule_ids.forEach((ruleId: string) => this.selectedRuleIds.push(ruleId));
    }
    if (data.ruleset_id) this.selectedRuleIds.push(data.ruleset_id);
    if (data.project_files) {
      data.project_files.forEach((f: any) => this.selectedFileIds.push(f.id));
    }
    this.userInputs[this.executionSteps[this.currentStepIndex]?.nodeId ?? ''] = data;

    // Track rich summaries for the loading screen display
    const currentInteractionType = this.executionSteps[this.currentStepIndex]?.interactionType;
    if (currentInteractionType === 'rule_select') {
      const selectedNames = Array.isArray(data.rule_names)
        ? data.rule_names as string[]
        : [];
      const selectedIds = Array.isArray(data.rule_ids)
        ? data.rule_ids as string[]
        : [];
      const fallbackType = data.ruleset_type ?? data.rule_type ?? 'IDS';

      if (selectedIds.length > 0) {
        this.selectedRulesData = selectedIds.map((ruleId, index) => ({
          name: selectedNames[index] || (ruleId === data.rule_id ? data.rule_name : ruleId),
          type: fallbackType,
        }));
      } else if (data.rule_name || data.rule_id) {
        this.selectedRulesData = [{
          name: data.rule_name ?? data.rule_id,
          type: fallbackType,
        }];
      }
    }
    if (
      (currentInteractionType === 'file_select'
        || currentInteractionType === 'file_selector'
        || currentInteractionType === 'file_selection')
      && Array.isArray(data.project_files)
    ) {
      this.selectedFilesData = this.mapToSelectedFiles(data.project_files);
    }

    // Override checkType if hybrid ruleset was selected
    if (data.ruleset_type === 'HYBRID') {
      this.definitionCheckType = 'HYBRID';
    } else if (data.ruleset_type === 'KI') {
      // KI rulesets run through the hoai/** module chain
      this.definitionCheckType = 'AI';
    }

    // Advance stepper
    this.completedSteps = [...this.completedSteps, this.currentStepIndex];
    const completedNodeId = this.executionSteps[this.currentStepIndex]?.nodeId;
    this.currentStepIndex++;

    // Mark the interactive step as completed in the vertical progress view
    if (completedNodeId) {
      this.progressSteps = this.progressSteps.map(s =>
        s.nodeId === completedNodeId ? {...s, status: 'completed' as const, helperText: 'Abgeschlossen'} : s
      );
      // Bewusst über updateProgressInfo: Eine eigene Prozentrechnung an dieser Stelle umging die
      // Bänder und die monotone Untergrenze und ließ den Balken beim Abschluss eines interaktiven
      // Schritts springen.
      this.updateProgressInfo();
    }

    this.myChecksService.resumeWithInput(this.instanceId, data).subscribe({
      error: (err) => {
        this.errorMessage = 'Fehler beim Fortsetzen: ' + (err?.error?.message || err?.message || '');
        this.navigateToLog();
      },
    });
  }

  /** Öffnet die Log-Ansicht der fehlgeschlagenen Prüfung in einem neuen Tab. */
  onViewLogs() {
    if (!this.instanceId) return;
    const route = this.router.createUrlTree(
      ['projects', this.projectId, 'check-runs', 'log'],
      {queryParams: {instanceId: this.instanceId}},
    );
    window.open(this.router.serializeUrl(route), '_blank');
  }

  onRetry() {
    this.subscription?.unsubscribe();
    this.stopElapsedTimer();
    this.currentStepIndex = 0;
    this.completedSteps = [];
    this.currentModuleId = '';
    this.selectedRuleIds = [];
    this.selectedFileIds = [];
    this.userInputs = {};
    this.selectedRulesData = [];
    this.selectedFilesData = [];
    this.progressSteps = [];
    this.progressLogs = [];
    this.progressFloor = 0;
    this.unitStepNodeId = undefined;
    this.pendingRuleData = undefined;
    this.pendingFileData = undefined;
    this.pendingTriggerData = undefined;
    this.instanceId = undefined;
    this.checkResultId = undefined;
    this.savingInProgress = false;
    // Zurück zum kombinierten Auswahlscreen, falls vorhanden – sonst direkt neu starten
    if (this.selectionPlan) {
      this.currentView = 'selection';
    } else {
      this.startExecution();
    }
  }

  /** Handles the current instance status after resume. */
  private handleInstanceStatus(instance: any) {
    this.applyInstanceMetadata(instance);
    switch (instance.status) {
      case 'COMPLETED':
        this.currentView = 'loading';
        this.loadingMessage = this.isLogRoute ? 'Ausführungsprotokoll' : 'Bericht wird geöffnet...';
        this.applyCheckTypeFromOutputData(instance.outputData);
        if (!this.isLogRoute) {
          // Abgeschlossene Prüfung → direkt auf die geteilte Berichtsseite umleiten.
          this.navigateToReport();
          break;
        }
        this.progressSteps = this.progressSteps.map(s => ({...s, status: 'completed' as const}));
        this.progressInfo = {
          ...this.progressInfo,
          currentStep: this.progressSteps.length,
          percentage: 100,
          status: 'COMPLETED',
          endedAtIso: this.toIsoString(instance.completedAt),
          endedAt: this.toDisplayTime(instance.completedAt),
        };
        break;
      case 'FAILED':
        this.currentView = 'loading';
        this.loadingMessage = 'Ausführungsprotokoll';
        this.errorMessage = instance.errorInfo || 'Prüfung fehlgeschlagen';
        this.errorDetails = this.buildErrorDetailsFromInstance(instance);
        this.progressInfo = {
          ...this.progressInfo,
          status: 'FAILED',
          endedAtIso: this.toIsoString(instance.completedAt),
          endedAt: this.toDisplayTime(instance.completedAt),
        };
        // Fehlschlag beim Reload/Fortsetzen erkannt → Credits erstatten (idempotent).
        this.refundCreditsForFailedRun();
        // Direkt ins Ausführungsprotokoll wechseln statt einer Fehlerseite.
        this.navigateToLog();
        break;
      case 'CANCELLED':
        this.currentView = 'loading';
        this.loadingMessage = 'Ausführungsprotokoll';
        this.errorMessage = 'Prüfung wurde abgebrochen';
        this.progressInfo = {
          ...this.progressInfo,
          status: 'CANCELLED',
          endedAtIso: this.toIsoString(instance.completedAt),
          endedAt: this.toDisplayTime(instance.completedAt),
        };
        this.navigateToLog();
        break;
      default:
        // RUNNING or PAUSED – subscribe to SSE for live updates.
        // Use the instance's actual start time so the elapsed timer survives page reloads
        // instead of restarting at 0:00.
        if (instance.startedAt) {
          const startedMs = new Date(instance.startedAt).getTime();
          if (!Number.isNaN(startedMs)) {
            this.executionStartTime = startedMs;
          }
        }
        if (!this.executionStartTime) {
          this.executionStartTime = Date.now();
        }
        this.startElapsedTimer();
        // Die Log-Ansicht ist rein lesend: NICHT per SSE subscriben. Ein zweiter Subscriber würde
        // beim Backend den gepufferten Event-Buffer (eventBuffer.remove) übernehmen und dem
        // ausführenden Tab Events (z. B. AWAITING_INPUT/COMPLETED) entziehen – der Check bliebe dort
        // hängen. Stattdessen nur read-only pollen, um die Anzeige zu aktualisieren.
        if (this.isLogRoute) {
          this.startPolling();
        } else {
          this.subscribeToEvents();
        }
        this.progressInfo = {...this.progressInfo, status: 'RUNNING'};
        break;
    }
  }

  private initWorkflow(workflowSlug?: string) {
    if (this.definitionId) {
      this.loadDefinitionAndStart(this.definitionId);
    } else if (workflowSlug) {
      this.workflowSlug = workflowSlug;
      this.resolveDefinitionBySlug(workflowSlug);
    } else {
      this.currentView = 'failed';
      this.errorMessage = 'Keine Prüfung angegeben (definitionId oder workflow fehlt)';
    }
  }

  onCancel() {
    if (this.instanceId) {
      this.myChecksService.cancelCheck(this.instanceId).subscribe();
    }
    this.navigateBack();
  }

  /**
   * Ein Protokolleintrag wurde aufgeklappt: Details nachladen.
   *
   * Genau das war die Ursache des Einfrierens – vorher hing der komplette Modul-Output an JEDEM
   * Eintrag, obwohl im Regelfall keiner aufgeklappt wird.
   */
  onLogDetailRequested(log: LogEntry) {
    const stepLogId = log.stepLogId;
    if (!this.instanceId || !stepLogId) return;
    this.activeLogDetail = {stepLogId, state: 'loading', rows: []};
    this.flowService.getInstanceLogStepDetail(this.instanceId, stepLogId).subscribe({
      next: (detail) => {
        // Ein zwischenzeitlich anderer Eintrag wurde geöffnet – diese Antwort ist überholt.
        if (this.activeLogDetail?.stepLogId !== stepLogId) return;
        this.activeLogDetail = {stepLogId, state: 'ready', rows: this.buildDetailRows(detail)};
      },
      error: () => {
        if (this.activeLogDetail?.stepLogId !== stepLogId) return;
        this.activeLogDetail = {
          stepLogId,
          state: 'error',
          rows: [],
          message: 'Details konnten nicht geladen werden.',
        };
      },
    });
  }

  onClose() {
    this.navigateBack();
  }

  /**
   * Leitet bei einem technischen Fehlschlag während der Ausführung direkt ins Ausführungsprotokoll
   * um (analog zu navigateToReport()), statt die Fehlerseite zu zeigen. No-op in der Log-Ansicht
   * selbst oder ohne Instanz (dann existiert kein Protokoll, das angezeigt werden könnte).
   */
  private navigateToLog() {
    if (this.isLogRoute || !this.instanceId) return;
    this.router.navigate(
      ['projects', this.projectId, 'check-runs', 'log'],
      {queryParams: {instanceId: this.instanceId}},
    );
  }

  onLogDetailClosed() {
    this.activeLogDetail = null;
  }

  /**
   * Sucht den Check-Result-Eintrag zur aktuell angezeigten Instanz und setzt `reportId` nur,
   * wenn dafür tatsächlich ein Bericht existiert (nicht bei fehlgeschlagenen/laufenden Prüfungen
   * ohne Ergebnis) – analog zu getReportId()/getReportStatus() in reports.component.ts.
   */
  private resolveReportIdForInstance(instanceId: string) {
    this.checkResultService.getCheckResultsAsSimpleChecks(this.projectId, 'ALL').subscribe({
      next: (checks) => {
        const match = checks.find(check => check.workflowInstanceId === instanceId);
        if (!match) return;
        const reportId = match.checkResultId || match.id;
        if (reportId && this.getReportStatusFor(match) !== 'FAILED') {
          this.reportId = reportId;
        }
      },
      error: () => {
        // Kein Bericht auflösbar → Button bleibt ausgeblendet.
      },
    });
  }

  private getReportStatusFor(check: SimpleCheck): string {
    if (check.reportStatus === 'COMPLETED' || check.reportStatus === 'RUNNING'
      || check.reportStatus === 'FAILED' || check.reportStatus === 'ARCHIVED') {
      return check.reportStatus;
    }
    if (check.archived) return 'ARCHIVED';
    if (check.failed) return 'FAILED';
    if (check.completed) return 'COMPLETED';
    return 'RUNNING';
  }

  /** Navigiert zurück zur myChecks-Übersicht (Kopfleisten-Zurück-Button). */
  goBack() {
    this.navigateBack();
  }

  ngOnDestroy() {
    this.stopPolling();
    this.stopElapsedTimer();
    this.subscription?.unsubscribe();
    this.commSubscription?.unsubscribe();
    this.routeSubscription?.unsubscribe();
  }

  private loadDefinitionAndStart(definitionId: string) {
    this.loadingMessage = 'Prüfung wird geladen...';
    this.flowService.getDefinition(definitionId).subscribe({
      next: (def) => {
        this.checkName = def.name ?? '';
        this.workflowSlug = def.slug;
        this.checkVersion = def.version;
        this.definitionCheckType = this.deriveCheckType(def.slug, (def as any).definitionType, def.manifestJson);
        this.loadSelectedRulesFromManifest(def.manifestJson);
        this.loadReportDisplayConfigFromManifest(def.manifestJson);
        this.buildStepperFromManifest(def.manifestJson);
        this.prepareSelectionOrStart(def.manifestJson);
      },
      error: () => this.startExecution(), // Fallback: start without manifest info
    });
  }

  private resolveDefinitionBySlug(slug: string) {
    this.loadingMessage = 'Prüfung wird geladen...';
    this.flowService.getDefinitionBySlug(slug).subscribe({
      next: (def) => {
        this.definitionId = def.id;
        this.checkName = def.name ?? '';
        this.workflowSlug = def.slug;
        this.checkVersion = def.version;
        this.definitionCheckType = this.deriveCheckType(def.slug, (def as any).definitionType, def.manifestJson);
        this.loadSelectedRulesFromManifest(def.manifestJson);
        this.loadReportDisplayConfigFromManifest(def.manifestJson);
        this.buildStepperFromManifest(def.manifestJson);
        this.prepareSelectionOrStart(def.manifestJson);
      },
      error: (err) => {
        this.currentView = 'failed';
        this.errorMessage = `Prüfung "${slug}" konnte nicht geladen werden: ` + (err?.error?.message || err?.message || '');
      },
    });
  }

  /**
   * Zeigt – sofern es sich um einen myCheck mit Regel-/Dateiauswahl handelt – den kombinierten
   * Vorab-Auswahlscreen. Andernfalls wird der Workflow direkt gestartet.
   */
  private prepareSelectionOrStart(manifestJson?: string) {
    const plan = this.buildSelectionPlan(manifestJson);
    if (plan) {
      this.selectionPlan = plan;
      this.currentView = 'selection';
      this.currentStepIndex = 0;
    } else {
      this.startExecution();
    }
  }

  /**
   * Leitet aus dem Manifest ab, wie die kombinierte Auswahl initialisiert werden soll.
   * Gibt null zurück, wenn das Manifest nicht dem myCheck-Muster (rule_selection + file_selection) entspricht.
   */
  private buildSelectionPlan(manifestJson?: string): CombinedSelectionPlan | null {
    if (!manifestJson) return null;
    try {
      const manifest: Manifest = JSON.parse(manifestJson);
      const steps = manifest.steps ?? [];
      const ruleStep = steps.find(s => s.moduleId === 'rule_selection' || s.moduleId === 'check_selector');
      const fileStep = steps.find(s => s.moduleId === 'file_selection' || s.moduleId === 'file_selector');
      if (!ruleStep && !fileStep) return null;

      const ruleSettings = ruleStep?.settings ?? {};
      const ruleConfig = ruleStep?.interactionRequired?.config ?? {};
      const fileSettings = fileStep?.settings ?? {};
      const fileConfig = fileStep?.interactionRequired?.config ?? {};

      const ruleMode: 'manual' | 'predefined' = ruleStep?.interactionRequired ? 'manual' : 'predefined';
      const fileMode: 'manual' | 'predefined' = fileStep?.interactionRequired ? 'manual' : 'predefined';

      const ruleTypes = this.asStringArray(ruleSettings['ruleTypes'] ?? ruleConfig['ruleTypes']);
      const rulesetType = (ruleSettings['selectedRulesetType'] as string)
        ?? (ruleTypes.includes('HYBRID') ? 'HYBRID' : ruleTypes.includes('KI') ? 'KI' : 'IDS');

      const fileTypes = this.asStringArray(fileSettings['fileTypes'] ?? fileConfig['fileTypes']);
      const documentClasses = this.asStringArray(fileSettings['documentClasses'] ?? fileConfig['documentClasses']);
      const predefinedFileSelectionScope: 'all' | 'selected' =
        fileSettings['predefinedFileSelectionScope'] ?? 'all';

      // fileTypes direkt aus dem Manifest verwenden – bei Scope 'all' stehen dort alle Typen,
      // bei 'selected' nur die konfigurierten. Fallback auf IFC nur im manuellen Modus.
      const effectiveFileTypes = fileTypes.length > 0 ? fileTypes : (fileMode === 'manual' ? ['IFC'] : []);

      // Extract report configuration for the summary display
      const reportConfig = manifest.trigger?.config?.['reportConfig'] ?? {};
      const reportFormats: string[] = [];
      if (reportConfig['excelExport']) reportFormats.push('EXCEL');
      if (reportConfig['pdfExport']) reportFormats.push('PDF');
      if (reportConfig['bcfExport']) reportFormats.push('BCF');

      // Use the first reportConfig perspective as the plan perspective (all sets share one perspective in practice)
      const reportConfigs: any[] = reportConfig['reportConfigs'] ?? [];
      const reportPerspective: string = reportConfigs[0]?.perspective ?? 'rules_first';

      return {
        checkName: manifest.name ?? this.checkName,
        checkDescription: manifest.description ?? '',
        userHint: (fileSettings['userHint'] ?? fileConfig['userHint'] ?? '').toString().trim(),
        triggerLabel: this.getTriggerLabel(manifest.trigger?.moduleId),
        ruleMode,
        fileMode,
        ruleTypes,
        selectedRuleSetIds: this.asStringArray(ruleSettings['selectedRuleSetIds'] ?? ruleConfig['selectedRuleSetIds']),
        selectedRuleIds: this.asStringArray(ruleSettings['selectedRuleIds'] ?? ruleConfig['selectedRuleIds']),
        partiallySelectedRuleSetIds: this.asStringArray(ruleSettings['partiallySelectedRuleSetIds'] ?? ruleConfig['partiallySelectedRuleSetIds']),
        fileTypes: effectiveFileTypes,
        documentClasses,
        minFiles: this.asNumber(fileSettings['minFiles'] ?? fileConfig['minFiles']),
        maxFiles: this.asNumber(fileSettings['maxFiles'] ?? fileConfig['maxFiles']),
        predefinedFileSelectionScope,
        rulesetType,
        reportFormats,
        reportPerspective,
        // Steht der Dispatcher im Manifest, läuft pro Regelset ein eigener Sub-Run – und die
        // Credits werden pro Regelset abgerechnet (Anzeige im Bestätigungs-Popup).
        usesMultiCheckDispatcher: steps.some(
          s => String(s.moduleId).toLowerCase() === 'multi_check_executor'),
      };
    } catch {
      return null;
    }
  }

  private getTriggerLabel(moduleId?: string): string {
    switch (moduleId) {
      case 'file_upload_trigger':
        return 'Datei-Upload';
      case 'schedule_trigger':
        return 'Zeitplan';
      case 'webhook_trigger':
        return 'Webhook';
      default:
        return 'Manuell';
    }
  }

  /** Wird vom kombinierten Auswahlscreen aufgerufen, wenn der Nutzer "Prüfung starten" klickt. */
  onStartCheck(result: CombinedSelectionResult) {
    // pendingRuleData/pendingFileData bleiben als Fallback für AWAITING_INPUT-Auto-Resume erhalten,
    // falls der Backend-Seitige Pre-Supply-Check für diesen Workflow nicht greift.
    this.pendingRuleData = result.ruleData;
    this.pendingFileData = result.fileData;
    this.selectedRulesData = result.ruleSummaries ?? [];
    this.selectedFilesData = result.fileSummaries ?? [];

    // Regel- und Datei-Auswahl direkt in die Trigger-Daten einbetten, damit das Backend
    // die interaktiven Steps ohne SSE-Round-Trip auflösen kann.
    let triggerData: Record<string, unknown> = result.triggerData ?? {};
    if (result.ruleData && Object.keys(result.ruleData).length > 0) {
      triggerData = {...triggerData, ...result.ruleData};
    }
    if (result.fileData) {
      triggerData = {...triggerData, ...result.fileData};
    }
    this.pendingTriggerData = Object.keys(triggerData).length > 0 ? triggerData : undefined;
    // Optionaler Berichtsname aus dem Bestätigungs-Popup. Er reist NICHT in den triggerData mit,
    // sondern als eigenes Feld des Trigger-Requests: er beschreibt den Lauf, nicht die Eingaben
    // eines Moduls.
    this.pendingReportName = result.reportName;

    // Ausgewählte IDs für die CheckResult-Persistierung vormerken.
    if (result.ruleData) {
      if (result.ruleData['rule_id']) this.selectedRuleIds.push(String(result.ruleData['rule_id']));
      if (Array.isArray(result.ruleData['rule_ids'])) {
        (result.ruleData['rule_ids'] as string[]).forEach(id => this.selectedRuleIds.push(id));
      }
    }
    const filesToTrack = result.fileData?.['project_files'] ?? result.triggerData?.['project_files'];
    if (Array.isArray(filesToTrack)) {
      (filesToTrack as any[]).forEach(f => { if (f.id) this.selectedFileIds.push(f.id); });
    }
    this.startExecution();
  }

  /**
   * Setzt einen interaktiven Schritt automatisch mit den bereits gesammelten Daten fort
   * (kombinierter Auswahlscreen), ohne ein Zwischen-UI zu zeigen.
   */
  private autoResumeInteraction(event: WorkflowExecutionEvent, data: Record<string, unknown>) {
    if (!this.instanceId) return;
    this.currentView = 'loading';
    this.loadingMessage = 'Auswahl wird übernommen – Prüfung startet …';

    if (event.nodeId) {
      this.progressSteps = this.progressSteps.map(s =>
        s.nodeId === event.nodeId ? {...s, status: 'running' as const, helperText: 'Auswahl übernommen'} : s,
      );
    }

    // Auswahl für die spätere CheckResult-Persistierung übernehmen
    if (data['rule_id']) this.selectedRuleIds.push(String(data['rule_id']));
    if (Array.isArray(data['rule_ids'])) data['rule_ids'].forEach((id: any) => this.selectedRuleIds.push(String(id)));
    if (data['ruleset_id']) this.selectedRuleIds.push(String(data['ruleset_id']));
    if (Array.isArray(data['project_files'])) {
      (data['project_files'] as any[]).forEach(f => this.selectedFileIds.push(f.id));
    }
    if (data['ruleset_type'] === 'HYBRID') this.definitionCheckType = 'HYBRID';
    else if (data['ruleset_type'] === 'KI') this.definitionCheckType = 'AI';

    this.myChecksService.resumeWithInput(this.instanceId, data).subscribe({
      error: (err) => {
        this.errorMessage = 'Fehler beim Fortsetzen: ' + (err?.error?.message || err?.message || '');
        this.navigateToLog();
      },
    });
  }

  /**
   * Builds dynamic stepper steps from the workflow manifest.
   * Interactive steps get a label based on their type, module steps are grouped as "Verarbeitung",
   * and a final "Ergebnis" step is always appended.
   */
  private buildStepperFromManifest(manifestJson?: string) {
    if (!manifestJson) {
      // Fallback to generic steps
      this.stepperSteps = [{label: 'Vorbereitung'}, {label: 'Verarbeitung'}, {label: 'Ergebnis'}];
      this.executionSteps = [];
      return;
    }

    try {
      const manifest: Manifest = JSON.parse(manifestJson);
      const steps = manifest.steps ?? [];
      const execSteps: ExecutionStepInfo[] = [];
      const stepperSteps: StepperStep[] = [];

      let lastModuleGroupAdded = false;

      for (const step of steps) {
        if (step.interactionRequired) {
          lastModuleGroupAdded = false;
          // Use module registry name first, then fallback to interaction type label
          const label = this.getStepLabel(step.nodeId!, step.moduleId)
            || this.getInteractionLabel(step.interactionRequired.type);
          execSteps.push({
            nodeId: step.nodeId!,
            label,
            type: 'interactive',
            interactionType: step.interactionRequired.type,
            moduleId: step.moduleId,
          });
          stepperSteps.push({label, nodeId: step.nodeId});
        } else {
          const label = this.getStepLabel(step.nodeId!, step.moduleId);
          if (!lastModuleGroupAdded) {
            // Group consecutive module steps as one "Verarbeitung" stepper entry
            execSteps.push({
              nodeId: step.nodeId!,
              label,
              type: 'module',
              moduleId: step.moduleId,
            });
            stepperSteps.push({label: 'Verarbeitung', nodeId: step.nodeId});
            lastModuleGroupAdded = true;
          } else {
            // Additional module steps don't add a new stepper entry, but are tracked
            execSteps.push({
              nodeId: step.nodeId!,
              label,
              type: 'module',
              moduleId: step.moduleId,
            });
          }
        }
      }

      // Always add a result step at the end
      execSteps.push({nodeId: '__result__', label: 'Ergebnis', type: 'result'});
      stepperSteps.push({label: 'Ergebnis'});

      this.executionSteps = execSteps;
      this.stepperSteps = stepperSteps;
      this.seedProgressStepsFromManifest();
    } catch {
      this.stepperSteps = [{label: 'Vorbereitung'}, {label: 'Verarbeitung'}, {label: 'Ergebnis'}];
      this.executionSteps = [];
    }
  }

  /**
   * Legt {@link progressSteps} vollständig aus dem Manifest an – VOR dem Start des Laufs.
   *
   * Ohne das entstand die Liste erst nach und nach aus den eintreffenden `STEP_RUNNING`-Ereignissen.
   * Der Nenner der Fortschrittsrechnung wuchs damit mit: Waren fünf Schritte gesehen und vier davon
   * fertig, zeigte der Balken 90 % – völlig unabhängig davon, wie viel Arbeit noch bevorstand.
   * Schritte, die nicht im Manifest stehen, hängt {@link updateProgressStepRunning} weiterhin an.
   *
   * Nebenbei wird hier der Knoten festgehalten, der die Einzelprüfungen trägt (siehe
   * {@link UNIT_CARRYING_MODULES}); er ist der Bezugspunkt aller drei Bänder.
   */
  private seedProgressStepsFromManifest() {
    // Nur für den laufenden Prüflauf. Die Protokollansicht ist rein lesend und baut ihre Schrittliste
    // aus den tatsächlich vorhandenen StepLogs auf – dort würden vorbelegte Schritte, die dieser Lauf
    // wegen einer Verzweigung nie ausgeführt hat, als erledigt erscheinen.
    if (this.isLogRoute) return;
    const steps = this.executionSteps.filter(s => s.type !== 'result');
    if (steps.length === 0) return;
    this.progressSteps = steps.map(s => ({
      nodeId: s.nodeId,
      label: s.label,
      status: 'pending' as const,
    }));
    this.unitStepNodeId = this.resolveUnitStepNodeId();
  }

  /** Der Knoten aus dem Manifest, der die Einzelprüfungen trägt (siehe {@link UNIT_CARRYING_MODULES}). */
  private resolveUnitStepNodeId(): string | undefined {
    return this.executionSteps.find(s => s.type !== 'result' && carriesUnits(s.moduleId))?.nodeId;
  }

  private getInteractionLabel(type?: string): string {
    switch (type) {
      case 'rule_select':
        return 'Prüfregeln';
      case 'file_select':
        return 'Dateien';
      default:
        return 'Eingabe';
    }
  }

  private startExecution() {
    this.currentView = 'loading';
    this.loadingMessage = 'Prüfung wird gestartet...';
    this.refundRequested = false;
    this.initProgressTracking();

    // Mark the first step as running immediately for visual feedback
    if (this.progressSteps.length > 0) {
      this.progressSteps = this.progressSteps.map((s, i) =>
        i === 0 ? {...s, status: 'running' as const} : s
      );
      this.updateProgressInfo();
    }

    // Add initial log entry
    this.progressLogs = [{
      timestamp: new Date().toLocaleTimeString('de-DE', {hour: '2-digit', minute: '2-digit', second: '2-digit'}),
      level: 'INFO',
      message: `Prüfung "${this.checkName}" wird gestartet...`,
      occurredAtIso: new Date().toISOString(),
    }];

    this.myChecksService.executeCheckById(this.definitionId!, this.projectId, this.pendingTriggerData,
      this.pendingReportName).subscribe({
      next: (instance) => {
        this.pendingTriggerData = undefined;
        this.pendingReportName = undefined;
        this.instanceId = instance.id;
        // Credits werden beim Start abgebucht (Reservierung). Erst wenn das erfolgreich
        // ist, wird der Lauf sichtbar fortgesetzt/abonniert. Bei fehlendem Kontingent (402)
        // wird die soeben erzeugte Instanz abgebrochen, sodass keine Prüfung läuft.
        this.myChecksService.reserveCredits(instance.id!, this.projectId).subscribe({
          next: () => {
            // Die Credits sind jetzt abgebucht. Der State (und damit jede Credit-Anzeige) wird
            // beim Login/Projektwechsel geladen und sonst von nichts aktualisiert – ohne diesen
            // Refresh zeigt der nächste Start desselben myChecks den alten Stand an und es sieht
            // aus, als würde nicht abgebucht.
            this.refreshContingent();
            // Update URL to use instanceId (prevents restart on page reload).
            // skipNextExecutionReset ensures the queryParams subscription doesn't clear
            // pendingRuleData/pendingFileData needed for auto-resume.
            this.skipNextExecutionReset = true;
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: {instanceId: instance.id},
              replaceUrl: true,
            });
            this.subscribeToEvents();
          },
          error: (err) => {
            // Reservierung fehlgeschlagen → Lauf abbrechen, damit keine unbezahlte Prüfung läuft.
            this.myChecksService.cancelCheck(instance.id!).subscribe();
            this.currentView = 'failed';
            this.errorMessage = err?.status === 402
              ? 'Kein Kontingent verfügbar: Es sind keine Prüfungen mehr vorhanden, um diese Prüfung zu starten.'
              : 'Prüfung konnte nicht gestartet werden: ' + (err?.error?.message || err?.message || 'Abrechnung fehlgeschlagen');
          },
        });
      },
      error: (err) => {
        this.currentView = 'failed';
        this.errorMessage = 'Prüfung konnte nicht gestartet werden: ' + (err?.error?.message || err?.message || 'Unbekannter Fehler');
      },
    });
  }

  /**
   * Erstattet die beim Start abgebuchten Credits zurück, wenn der Prüflauf technisch
   * fehlgeschlagen ist. Read-only-Log-Ansichten lösen keine Abrechnung aus. Das Backend
   * erstattet ausschließlich bei Status FAILED und ist idempotent – ein manueller Abbruch
   * (CANCELLED) sowie ein erfolgreicher Lauf werden dort nicht erstattet.
   */
  private refundCreditsForFailedRun() {
    if (this.isLogRoute || !this.instanceId || this.refundRequested) return;
    this.refundRequested = true;
    this.myChecksService.refundCredits(this.instanceId, this.projectId).subscribe({
      next: () => this.refreshContingent(),
      error: (err) => {
        console.error('Credit-Erstattung fehlgeschlagen:', err);
        this.refundRequested = false;
      },
    });
  }

  /**
   * Lädt den State neu, nachdem sich das Kontingent geändert hat (Reservierung, Erstattung,
   * Fallback-Abrechnung beim Speichern des Ergebnisses).
   *
   * <p>Nötig, weil {@link StateConfigService} das Kontingent nur beim Laden des States setzt und
   * es sonst nichts aktualisiert: die Credit-Anzeigen (z.B. "Nach dieser Prüfung bleiben noch X
   * Credits übrig" in der Dateiauswahl) blieben sonst bis zum Reload auf dem Stand von vor dem
   * Lauf stehen – die Abbuchung in der Datenbank ist längst passiert.
   */
  private refreshContingent(): void {
    this.stateConfig.update().subscribe();
  }

  private subscribeToEvents() {
    if (!this.instanceId) return;

    this.subscription = this.myChecksService
      .subscribeToEvents(this.instanceId)
      .subscribe({
        next: (event) => this.handleEvent(event),
        error: (err) => {
          console.error('SSE connection error:', err);
          this.pollInstanceStatus(true);
        },
      });

    // Sicherheitsnetz: Polling läuft ab sofort parallel zur SSE-Verbindung, nicht erst ab dem
    // ersten STEP_RUNNING-Event. Bei sehr schnellen Checks (z.B. einzelne einfache IDS-Regel)
    // kann der Workflow bereits vollständig durchlaufen und alle Events gepuffert haben, bevor
    // die SSE-Verbindung überhaupt steht - ohne dieses Sicherheitsnetz bliebe der Ladebildschirm
    // dann endlos hängen, bis man manuell neu lädt (was resumeExistingInstance() korrekt auflöst).
    this.startPolling();
  }

  private handleEvent(event: WorkflowExecutionEvent) {
    switch (event.type) {
      case 'AWAITING_INPUT':
        this.stopPolling();
        // Mark all steps BEFORE this one as completed (safety net for missed events)
        this.markPrecedingStepsCompleted(event.nodeId);
        // Mark this interactive step as "running" in the progress view
        if (event.nodeId) {
          this.progressSteps = this.progressSteps.map(s =>
            s.nodeId === event.nodeId ? {...s, status: 'running' as const, helperText: 'Warte auf Eingabe...'} : s
          );
        }
        // Wurde die Auswahl bereits über den kombinierten Screen getroffen, automatisch fortsetzen
        {
          const promptType = event.prompt?.type;
          if (promptType === 'rule_select' && this.pendingRuleData !== undefined) {
            const data = this.pendingRuleData;
            this.pendingRuleData = undefined;
            this.autoResumeInteraction(event, data);
            break;
          }
          if (promptType === 'file_select' && this.pendingFileData !== undefined) {
            const data = this.pendingFileData;
            this.pendingFileData = undefined;
            this.autoResumeInteraction(event, data);
            break;
          }
        }
        this.showInteractiveStep(event);
        break;
      case 'STEP_RUNNING':
        this.currentView = 'loading';
        this.currentStepInfo = {nodeId: event.nodeId, moduleId: event.moduleId};
        this.currentModuleId = event.moduleId ?? '';
        this.loadingMessage = this.getSubRunMessage(event.subRun)
          ?? this.getLoadingMessage(event.moduleId, event.nodeId);
        this.applySubRunProgress(event.subRun);
        this.refreshPhaseMessage();
        // Mark all steps BEFORE this one as completed (safety net for missed events)
        this.markPrecedingStepsCompleted(event.nodeId);
        this.updateStepperForNode(event.nodeId, false);
        this.updateProgressStepRunning(event);
        this.startPolling();
        break;
      case 'STEP_COMPLETED':
        this.updateStepperForNode(event.nodeId, true);
        this.updateProgressStepCompleted(event);
        break;
      case 'PROGRESS':
        // Beschleuniger, nicht Wahrheit: Die SSE-Emitter liegen pro Backend-Replika im Speicher,
        // dieser Stream hängt also regelmäßig an einer anderen Replika als die, die publiziert.
        // Verbindlich ist der /progress-Poll (siehe startProgressPolling).
        this.applyUnitProgress(event.progress);
        break;
      case 'COMPLETED':
        this.stopPolling();
        this.stopElapsedTimer();
        this.currentView = 'loading';
        this.loadingMessage = this.isLogRoute ? 'Ausführungsprotokoll' : 'Bericht wird geöffnet...';
        this.finalizeSubRunProgress(false);
        this.applyCheckTypeFromOutputData(event.outputData);
        this.subscription?.unsubscribe();
        if (!this.isLogRoute) {
          this.outputData = event.outputData;
          // Ergebnis für die Liste persistieren und direkt auf die geteilte Berichtsseite umleiten.
          this.saveCheckResult(event.outputData);
          this.navigateToReport();
          break;
        }
        // Log-Ansicht ist rein lesend: nur den Fortschritt für die Protokollanzeige vervollständigen.
        this.progressSteps = this.progressSteps.map(s => ({...s, status: 'completed' as const}));
        this.progressInfo = {
          ...this.progressInfo,
          currentStep: this.progressSteps.length,
          percentage: 100,
          status: 'COMPLETED',
          endedAtIso: new Date().toISOString(),
          endedAt: this.toDisplayTime(new Date().toISOString()),
        };
        break;
      case 'FAILED':
        this.stopPolling();
        this.stopElapsedTimer();
        this.currentView = 'loading';
        this.loadingMessage = 'Ausführungsprotokoll';
        this.errorMessage = event.error || 'Unbekannter Fehler';
        this.finalizeSubRunProgress(true);
        this.progressInfo = {
          ...this.progressInfo,
          status: 'FAILED',
          endedAtIso: new Date().toISOString(),
          endedAt: this.toDisplayTime(new Date().toISOString()),
        };
        this.subscription?.unsubscribe();
        // Technischer Fehlschlag → beim Start abgebuchte Credits erstatten.
        this.refundCreditsForFailedRun();
        // Direkt ins Ausführungsprotokoll wechseln statt einer Fehlerseite.
        this.navigateToLog();
        break;
    }

    // Collect log entries from events if present
    this.collectLogFromEvent(event);
  }

  /**
   * Marks all progress steps BEFORE the given nodeId as completed.
   * This handles cases where STEP_COMPLETED events for earlier steps were missed
   * (e.g., internal steps that completed before SSE subscription).
   */
  private markPrecedingStepsCompleted(nodeId?: string) {
    if (!nodeId) return;
    const idx = this.progressSteps.findIndex(s => s.nodeId === nodeId);
    if (idx <= 0) return;

    let changed = false;
    this.progressSteps = this.progressSteps.map((s, i) => {
      if (i < idx && s.status !== 'completed') {
        changed = true;
        return {...s, status: 'completed' as const, helperText: s.helperText || 'Abgeschlossen'};
      }
      return s;
    });

    if (changed) {
      this.updateProgressInfo();
    }
  }

  private showInteractiveStep(event: WorkflowExecutionEvent) {
    const interactionType = event.prompt?.type;
    if (!interactionType) {
      this.currentView = 'failed';
      this.errorMessage = 'Ungültiger interaktiver Schritt (kein Typ angegeben)';
      return;
    }

    const component = resolveStepComponent(interactionType);
    if (component) {
      this.activeInteractionComponent = component;
      this.activeInteractionInputs = {
        config: event.prompt?.config,
        projectId: this.projectId,
      };
      this.currentView = 'interactive';
    } else {
      // No registered component for this interaction type – show error with info
      this.currentView = 'failed';
      this.errorMessage = `Unbekannter Interaktionstyp: "${interactionType}". Bitte aktualisieren Sie die Anwendung.`;
    }

    this.currentPrompt = event.prompt;
    this.updateStepperForNode(event.nodeId, false);
  }

  /**
   * Updates the stepper position based on the current node ID from SSE events.
   */
  private updateStepperForNode(nodeId?: string, markCompleted = false) {
    if (!nodeId || this.executionSteps.length === 0) return;

    // Find the stepper index for this node
    const execIndex = this.executionSteps.findIndex(s => s.nodeId === nodeId);
    if (execIndex === -1) return;

    // Map execution step index to stepper index (stepper may have fewer entries due to grouping)
    const stepperIndex = this.findStepperIndexForExecStep(execIndex);
    if (stepperIndex >= 0) {
      this.currentStepIndex = stepperIndex;
      if (markCompleted && !this.completedSteps.includes(stepperIndex)) {
        this.completedSteps = [...this.completedSteps, stepperIndex];
      }
    }
  }

  private findStepperIndexForExecStep(execIndex: number): number {
    const targetNodeId = this.executionSteps[execIndex]?.nodeId;
    // Find the matching stepper step by nodeId
    const idx = this.stepperSteps.findIndex(s => s.nodeId === targetNodeId);
    if (idx >= 0) return idx;

    // If not found directly (grouped module steps), find the nearest previous stepper entry
    for (let i = execIndex; i >= 0; i--) {
      const nid = this.executionSteps[i]?.nodeId;
      const sIdx = this.stepperSteps.findIndex(s => s.nodeId === nid);
      if (sIdx >= 0) return sIdx;
    }
    return this.currentStepIndex;
  }

  private saveCheckResult(outputData: any) {
    if (!outputData || this.checkResultId || this.savingInProgress) return;
    this.savingInProgress = true;
    // Das tatsächliche Ergebnis hat Vorrang vor dem aus dem Manifest geratenen Typ: ein
    // Dispatcher-Manifest liefert dort immer 'MIXED', auch wenn zur Laufzeit nur ein einziges
    // IDS-Regelset gewählt wurde. Kann der Output keinen Typ hergeben, greift der Manifest-Wert.
    const resolvedCheckType =
      this.inferCheckTypeFromOutputData(outputData)
      ?? this.definitionCheckType
      ?? 'CUSTOM';
    this.definitionCheckType = resolvedCheckType;

    const body: { [key: string]: any } = {
      checkType: resolvedCheckType,
      ruleIds: [...new Set(this.selectedRuleIds)],
      fileIds: [...new Set(this.selectedFileIds)],
      results: outputData,
      score: outputData?.score ?? null,
      totalCheckedElements: outputData?.totalCheckedElements ?? null,
      executionDurationMs: outputData?.execution_duration_ms ?? null,
      workflowInstanceId: this.instanceId || null,
    };

    this.checkResultService.createCheckResult(body, this.projectId).subscribe({
      next: (result) => {
        this.checkResultId = result.id;
        // Fallback-Abrechnung: wurde beim Start nicht reserviert, bucht das Backend hier ab.
        this.refreshContingent();
      },
      error: (err) => {
        console.error('Failed to save check result:', err);
        this.savingInProgress = false;
        if (err?.status === 402) {
          // Contingent des Projekt-Eigentümers hat keine Credits/AddOn-Credits/Trial mehr –
          // der Prüflauf ist zwar durchgelaufen, das Ergebnis konnte aber nicht abgerechnet
          // und gespeichert werden.
          this.currentView = 'failed';
          this.errorMessage = 'Kein Kontingent verfügbar: Das Ergebnis konnte nicht gespeichert werden, da keine Credits mehr vorhanden sind.';
        }
      },
    });
  }

  /**
   * Derives the check result type from the workflow slug.
   * Falls back to 'CUSTOM' for unknown slugs.
   */
  private deriveCheckType(slug?: string, definitionType?: string, manifestJson?: string): string {
    if (definitionType === 'HYBRID_RULESET' || definitionType === 'HYBRID_CHECK') return 'HYBRID';
    const fromManifest = this.deriveCheckTypeFromManifest(manifestJson);
    // Der Dispatcher hat Vorrang vor dem Slug: ein Lauf über `multi_check_executor` liefert
    // Teilberichte je Regelset ('MIXED'), egal wie der Slug heißt. Ein Slug wie "…loi…" oder
    // "…ids…" würde sonst einen Einzeltyp behaupten, und navigateToReport() gäbe ihn als `type`
    // mit – der Bericht landete im "unbekannter Typ"-Zweig.
    if (fromManifest === 'MIXED') return fromManifest;
    const fromSlug = this.deriveCheckTypeFromSlug(slug);
    if (fromSlug !== 'CUSTOM') return fromSlug;
    // Slug is inconclusive (e.g. custom builder checks) — inspect the manifest steps.
    return fromManifest ?? fromSlug;
  }

  private deriveCheckTypeFromSlug(slug?: string): string {
    if (!slug) return 'CUSTOM';
    if (slug.includes('hybrid')) return 'HYBRID';
    if (slug.includes('ids')) return 'IDS';
    if (slug.includes('hoai') || slug.includes('ai-check')) return 'AI';
    if (slug.includes('loi')) return 'LOI';
    if (slug.includes('quality')) return 'QUALITY';
    return 'CUSTOM';
  }

  /** Detect the check type from the manifest steps (KI/HOAI chain, hybrid executor, rule_selection settings). */
  private deriveCheckTypeFromManifest(manifestJson?: string): string | undefined {
    if (!manifestJson) return undefined;
    try {
      const manifest = JSON.parse(manifestJson);
      const steps: any[] = manifest.steps ?? [];
      // Zuerst prüfen: Der Dispatcher ersetzt die typspezifische Kette, der Lauf liefert einen
      // Teilbericht je Regelset (checkType 'MIXED') statt eines einzelnen typspezifischen Berichts.
      if (steps.some(s => String(s.moduleId).toLowerCase() === 'multi_check_executor')) {
        return 'MIXED';
      }
      if (steps.some(s => ['hoai_rule_resolver', 'hoai_check', 'hoai_result_aggregator', 'ai_rule_resolver', 'ai_check', 'ai_result_aggregator'].includes(s.moduleId))) {
        return 'AI';
      }
      if (steps.some(s => s.moduleId === 'HYBRID_RULESET_EXECUTOR')) {
        return 'HYBRID';
      }
      const ruleStep = steps.find(s => s.moduleId === 'rule_selection');
      const selectedType = ruleStep?.settings?.selectedRulesetType;
      if (selectedType === 'KI') return 'AI';
      if (selectedType === 'HYBRID') return 'HYBRID';
      const ruleTypes: string[] = ruleStep?.settings?.ruleTypes ?? [];
      if (ruleTypes.includes('KI')) return 'AI';
      if (ruleTypes.includes('HYBRID')) return 'HYBRID';
    } catch {
      return undefined;
    }
    return undefined;
  }

  private applyCheckTypeFromOutputData(outputData: any): void {
    const inferred = this.inferCheckTypeFromOutputData(outputData);
    if (inferred) {
      this.definitionCheckType = inferred;
    }
  }

  private inferCheckTypeFromOutputData(outputData: any): string | undefined {
    if (!outputData || typeof outputData !== 'object') return undefined;

    const explicitType = String(outputData.checkType ?? outputData.check_type ?? '').toUpperCase();
    if (explicitType === 'IDS' || explicitType === 'AI' || explicitType === 'HYBRID'
      || explicitType === 'MIXED' || explicitType === 'LOI' || explicitType === 'QUALITY') {
      return explicitType;
    }
    if (explicitType === 'KI' || explicitType === 'HOAI') {
      return 'AI';
    }

    // Dispatcher-Lauf OHNE expliziten Typ: ein Teilbericht je Regelset. Sind alle Teilberichte
    // vom selben Typ, ist das der Typ des Laufs (ein einzelnes IDS-Regelset ist kein "gemischter"
    // Lauf); nur bei echter Mischung bleibt es MIXED. Muss vor den Heuristiken unten greifen.
    if (Array.isArray(outputData.reports)) {
      const types = new Set<string>(
        outputData.reports
          .map((entry: any) => String(entry?.check_type ?? entry?.checkType ?? '').toUpperCase())
          .filter((type: string) => !!type),
      );
      return types.size === 1 ? [...types][0] : 'MIXED';
    }

    if (Array.isArray(outputData.specifications)) {
      return 'IDS';
    }

    if (Array.isArray(outputData.results)) {
      const hasIdsFileResults = outputData.results.some((entry: any) => {
        const candidate = entry?.outputs && typeof entry.outputs === 'object' ? entry.outputs : entry;
        return Array.isArray(candidate?.specifications);
      });
      if (hasIdsFileResults) {
        return 'IDS';
      }
    }

    if (Array.isArray(outputData.rules) || Array.isArray(outputData.check_results)) {
      return 'AI';
    }

    return undefined;
  }

  private navigateBack() {
    this.router.navigate(['projects', this.projectId, 'check-runs']);
  }

  // ─── Progress Tracking ──────────────────────────────────────────────────────

  private initProgressTracking() {
    const startedAt = new Date();
    this.executionStartTime = startedAt.getTime();
    // Fortschritts-Untergrenze für einen neuen Lauf zurücksetzen.
    this.progressFloor = 0;
    // Bewusst neu ABLEITEN statt zu leeren: initProgressTracking() läuft nach dem Aufbau des
    // Steppers, ein pauschales `undefined` würde den aus dem Manifest bestimmten Einheiten-Schritt
    // wieder wegwerfen – und der Balken fiele auf das Raten beim ersten Fortschrittsstand zurück.
    this.unitStepNodeId = this.resolveUnitStepNodeId();
    const project = this.stateConfig.getProject();
    const organisation = this.stateConfig.getOrganisation();
    this.progressInfo = {
      currentStep: 0,
      totalSteps: 0,
      elapsedSeconds: 0,
      percentage: 0,
      checkName: this.checkName,
      checkId: this.instanceId,
      startedAt: this.toDisplayTime(startedAt.toISOString()),
      startedAtIso: startedAt.toISOString(),
      organisationName: organisation?.name,
      projectName: project?.name,
      myCheckVersion: this.checkVersion,
      status: 'RUNNING',
    };

    // Build progress steps from ALL execution steps (interactive + module)
    // Interactive steps that were already completed show as "completed"
    // Module steps start as "pending"
    this.progressSteps = this.executionSteps
      .filter(s => s.type !== 'result')
      .map((s, index) => {
        const isInteractive = s.type === 'interactive';
        // Interactive steps before the current module phase are already completed
        const isAlreadyCompleted = isInteractive && index < this.currentStepIndex;
        return {
          nodeId: s.nodeId,
          label: s.label,
          status: isAlreadyCompleted ? 'completed' as const : 'pending' as const,
        };
      });

    this.progressInfo.totalSteps = this.progressSteps.length;

    // Update percentage for already completed interactive steps (einheitlich + monoton)
    this.updateProgressInfo();

    this.startElapsedTimer();
  }

  /** Maps a nodeId/moduleId to a human-readable step label using the module registry. */
  private getStepLabel(nodeId: string, moduleId?: string): string {
    // Primary source: module registry (like workflow-editor does)
    if (moduleId) {
      const registryModule = this.moduleRegistry.find(m => m.moduleId === moduleId);
      if (registryModule?.name) {
        return registryModule.name;
      }
    }

    // Fallback: static mapping for well-known modules
    const moduleLabels: Record<string, string> = {
      'rule_selection': 'Regelauswahl',
      'file_selection': 'Dateiauswahl',
      'check_data_preparation': 'Datenextraktion',
      'check_execution': 'Prüfregeln anwenden',
      'data_extraction': 'Datenextraktion',
      'classification': 'Klassifikation',
      'data_agent': 'Datenagent',
      'rule_engine': 'Prüfregeln anwenden',
      'aggregation': 'Aggregation',
      'report_generation': 'Bericht erstellen',
      'HYBRID_RULESET_EXECUTOR': 'Prüfregeln anwenden',
      'multi_check_executor': 'myChecks prüfen',
      'hoai_rule_resolver': 'Regelzuordnung',
      'hoai_check': 'KI Prüfung',
      'hoai_result_aggregator': 'Ergebnisse aggregieren',
      'ai_rule_resolver': 'Regelzuordnung',
      'ai_check': 'KI Prüfung',
      'ai_result_aggregator': 'Ergebnisse aggregieren',
    };

    if (moduleId && moduleLabels[moduleId]) {
      return moduleLabels[moduleId];
    }

    // Last fallback: nodeId as readable label (snake_case/kebab-case → Title Case)
    const source = moduleId || nodeId || '';
    return source
      .replace(/_/g, ' ')
      .replace(/-/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase())
      .trim() || 'Verarbeitung';
  }

  private startElapsedTimer() {
    if (this.isLogRoute) return;
    this.stopElapsedTimer();
    this.elapsedTimer = setInterval(() => {
      if (this.executionStartTime) {
        this.progressInfo = {
          ...this.progressInfo,
          elapsedSeconds: Math.floor((Date.now() - this.executionStartTime) / 1000),
        };
      }
    }, 1000);
    this.startProgressCreep();
  }

  private stopElapsedTimer() {
    if (this.elapsedTimer) {
      clearInterval(this.elapsedTimer);
      this.elapsedTimer = undefined;
    }
    this.stopProgressCreep();
  }

  /**
   * Zählt die Fortschrittsanzeige in den ersten Sekunden künstlich gleichmäßig hoch (alle 2 s um
   * 2 %, bis zur Obergrenze aus {@link creepCap}). Er überbrückt ausschließlich die Zeit, in der es
   * noch kein Signal gibt – Anlauf des Laufs und die Lücke zwischen Start des Einheiten-Schritts und
   * dem ersten Fortschrittsstand. Der Creep erhöht nur die monotone {@link progressFloor}; sobald
   * der echte Fortschritt darüber liegt, übernimmt dieser nahtlos.
   */
  private startProgressCreep() {
    if (this.isLogRoute) return;
    this.stopProgressCreep();
    this.progressCreepTimer = setInterval(() => {
      const cap = this.creepCap();
      if (this.progressFloor >= cap) {
        // Nicht abschalten: Läuft der Einheiten-Schritt an, bevor Zahlen da sind, steigt die
        // Obergrenze noch einmal (siehe creepCap) und der Balken darf weiterlaufen.
        return;
      }
      this.progressFloor = Math.min(cap, this.progressFloor + 2);
      // Neu berechnen (clamped gegen progressFloor) und Anzeige aktualisieren.
      this.updateProgressInfo();
    }, 2000);
  }

  /**
   * Obergrenze des Creeps im aktuellen Zustand: das Vorbereitungsband, und etwas darüber, sobald der
   * Einheiten-Schritt läuft, aber noch keinen Plan gemeldet hat. Sobald echte Zahlen da sind, ist
   * der Creep ohnehin abgeschaltet (siehe {@link applyUnitProgress}).
   */
  private creepCap(): number {
    const unitStep = this.unitStepNodeId
      ? this.progressSteps.find(s => s.nodeId === this.unitStepNodeId)
      : undefined;
    return unitStep?.status === 'running'
      ? MyChecksExecutionComponent.UNIT_BAND_CREEP_CAP
      : MyChecksExecutionComponent.PROGRESS_CREEP_CAP;
  }

  private stopProgressCreep() {
    if (this.progressCreepTimer) {
      clearInterval(this.progressCreepTimer);
      this.progressCreepTimer = undefined;
    }
  }

  private updateProgressStepRunning(event: WorkflowExecutionEvent) {
    const nodeId = event.nodeId;
    if (!nodeId) return;

    // Find or create step in progress list
    let step = this.progressSteps.find(s => s.nodeId === nodeId);
    if (!step) {
      // Dynamically add step if not from manifest
      step = {
        nodeId,
        label: this.getLoadingMessage(event.moduleId, nodeId).replace('...', ''),
        status: 'running',
      };
      this.progressSteps = [...this.progressSteps, step];
    } else {
      this.progressSteps = this.progressSteps.map(s =>
        s.nodeId === nodeId ? {...s, status: 'running' as const} : s
      );
    }

    // Update progress info
    this.updateProgressInfo();
  }

  private updateProgressStepCompleted(event: WorkflowExecutionEvent) {
    const nodeId = event.nodeId;
    if (!nodeId) return;

    this.progressSteps = this.progressSteps.map(s =>
      s.nodeId === nodeId ? {
        ...s,
        status: 'completed' as const,
        helperText: event.statusMessage || s.helperText,
        durationSeconds: event.durationMs ? Math.round(event.durationMs / 1000) : undefined,
      } : s
    );

    this.updateProgressInfo();
  }

  /**
   * Aktualisiert {@link progressInfo} aus dem aktuellen Zustand von {@link progressSteps} und den
   * gemeldeten Einzelprüfungen.
   *
   * Einheitliche Berechnung für ALLE Quellen (SSE-Events + Polling), damit der Balken nicht springt.
   * Das Ergebnis wird gegen {@link progressFloor} geclampt, sodass die Anzeige niemals zurückläuft,
   * und bis zum tatsächlichen Abschluss auf {@link TAIL_BAND_END} begrenzt.
   */
  private updateProgressInfo() {
    const total = this.progressSteps.length;
    const completedCount = this.progressSteps.filter(s => s.status === 'completed').length;
    const hasRunning = this.progressSteps.some(s => s.status === 'running');

    const capped = Math.min(
      Math.round(this.computeBandedPercentage()),
      MyChecksExecutionComponent.TAIL_BAND_END,
    );
    const percentage = Math.max(this.progressFloor, capped);
    this.progressFloor = percentage;
    this.progressInfo = {
      ...this.progressInfo,
      currentStep: hasRunning ? Math.min(completedCount + 1, total) : completedCount,
      totalSteps: total,
      percentage,
      checkName: this.checkName,
    };
  }

  /**
   * Der Fortschritt in Prozent, aufgeteilt in drei Bänder um den Einheiten-Schritt herum:
   *
   * <ul>
   *   <li>Vorbereitung (Einheiten-Schritt noch nicht gestartet): 0…{@link PREP_BAND_MAX} %,
   *       anteilig an den erledigten Vorbereitungsschritten.</li>
   *   <li>Einzelprüfungen (Einheiten-Schritt läuft): {@link PREP_BAND_MAX}…{@link UNIT_BAND_END} %,
   *       direkt aus `unitsDone / unitsTotal`. Genau das ist der Punkt: Bei 3 von 46 steht der
   *       Balken bei ~15 % und nicht bei 96 %.</li>
   *   <li>Aggregation und Bericht (Einheiten-Schritt fertig): {@link UNIT_BAND_END}…
   *       {@link TAIL_BAND_END} %, anteilig an den erledigten Folgeschritten.</li>
   * </ul>
   *
   * Ohne bekannten Einheiten-Schritt (Altlauf oder fehlendes Manifest) bleibt es bei der reinen
   * Schrittrechnung über das gesamte Band – dann ist nichts Besseres bekannt.
   */
  private computeBandedPercentage(): number {
    const prep = MyChecksExecutionComponent.PREP_BAND_MAX;
    const unitEnd = MyChecksExecutionComponent.UNIT_BAND_END;
    const tailEnd = MyChecksExecutionComponent.TAIL_BAND_END;

    const unitStep = this.unitStepNodeId
      ? this.progressSteps.find(s => s.nodeId === this.unitStepNodeId)
      : undefined;

    if (!unitStep) {
      return this.stepFraction(this.progressSteps) * tailEnd;
    }

    const unitIndex = this.progressSteps.indexOf(unitStep);
    if (unitStep.status === 'pending') {
      return this.stepFraction(this.progressSteps.slice(0, unitIndex)) * prep;
    }

    if (unitStep.status === 'running') {
      const fraction = this.unitFraction();
      // Kein Plan (noch keine Zahlen vom Backend): am unteren Rand des Bandes stehen bleiben und
      // den Creep die Lücke überbrücken lassen, statt eine Zahl zu erfinden.
      if (fraction === undefined) return prep;
      return prep + (unitEnd - prep) * fraction;
    }

    // Einheiten-Schritt abgeschlossen: nur noch Aggregation und Berichtserstellung.
    return unitEnd + (tailEnd - unitEnd) * this.stepFraction(this.progressSteps.slice(unitIndex + 1));
  }

  /**
   * Anteil erledigter Schritte in einem Abschnitt (0…1); ein laufender Schritt zählt halb, weil es
   * dort keinen messbaren Füllstand gibt. Ein leerer Abschnitt gilt als vollständig erledigt –
   * sonst bliebe das zugehörige Band für immer bei 0.
   */
  private stepFraction(steps: ExecutionStep[]): number {
    if (steps.length === 0) return 1;
    const done = steps.reduce((sum, s) => {
      if (s.status === 'completed') return sum + 1;
      if (s.status === 'running') return sum + 0.5;
      return sum;
    }, 0);
    return Math.min(done / steps.length, 1);
  }

  /**
   * Anteil der erledigten Einzelprüfungen (0…1), oder `undefined`, solange das Backend noch keinen
   * Einheiten-Plan gemeldet hat.
   *
   * Die angefangene Einheit zählt anteilig mit, wenn sie elementweise auffächert: Eine hybride Regel
   * über ein Raumbuch ist EINE Einheit, läuft aber minutenlang. Ohne diesen Anteil stünde der Balken
   * zwischen zwei Einheiten still, obwohl die Zeile darunter hochzählt – zwei Anzeigen, die dasselbe
   * meinen und sich widersprechen.
   */
  private unitFraction(): number | undefined {
    const total = this.progressInfo.unitsTotal;
    if (!total || total <= 0) return undefined;
    const done = Math.min(this.progressInfo.unitsDone ?? 0, total);
    return Math.min((done + this.partialUnitFraction()) / total, 1);
  }


  /**
   * Anteil der gerade laufenden Einheit (0…1), abgeleitet aus dem Element-Fortschritt der aktuellen
   * Prüfregel. 0, solange die Einheit nicht elementweise auffächert.
   */
  private partialUnitFraction(): number {
    const elementTotal = this.progressInfo.elementUnitsTotal;
    if (!elementTotal || elementTotal <= 0) return 0;
    const elementDone = Math.min(this.progressInfo.elementUnitsDone ?? 0, elementTotal);
    return elementDone / elementTotal;
  }

  /**
   * Übernimmt einen Fortschrittsstand aus `/progress` bzw. aus einem `PROGRESS`-SSE-Event.
   *
   * Beide Quellen liefern denselben Inhalt und können sich überholen (der Poll kann jünger sein als
   * ein verzögertes Event). Deshalb werden die Zähler nur nach oben übernommen – zusammen mit dem
   * bestehenden {@link progressFloor} kann die Anzeige damit nie zurücklaufen.
   */
  private applyUnitProgress(progress?: CheckProgress) {
    if (!progress) return;
    // Die Element-Skala zuerst und unabhängig: Sie existiert auch dann, wenn (noch) kein
    // Einheiten-Plan vorliegt, und darf nicht am `return` unten hängenbleiben.
    this.applyElementProgress(progress);

    const total = progress.unitsTotal ?? undefined;
    if (!total || total <= 0) return;

    // Rückfall für Läufe ohne auswertbares Manifest: Normalerweise steht der Einheiten-Schritt
    // längst fest (siehe seedProgressStepsFromManifest). Nur wenn er dort nicht gefunden wurde,
    // wird er hier aus dem gerade laufenden Schritt abgeleitet.
    if (!this.unitStepNodeId) {
      this.unitStepNodeId = this.progressSteps.find(s => s.status === 'running')?.nodeId;
    }

    const incomingDone = progress.unitsDone ?? 0;
    const knownDone = this.progressInfo.unitsDone ?? 0;
    this.progressInfo = {
      ...this.progressInfo,
      unitsTotal: total,
      unitsDone: Math.max(knownDone, Math.min(incomingDone, total)),
      unitsFailed: Math.max(this.progressInfo.unitsFailed ?? 0, progress.unitsFailed ?? 0),
    };
    this.refreshPhaseMessage();
    // Sobald echte Zahlen da sind, ist der künstliche Anlauf überflüssig – er würde den Balken sonst
    // über den tatsächlichen Stand hinausschieben und die Zahl darunter Lügen strafen.
    this.stopProgressCreep();
    this.updateProgressInfo();
  }

  /**
   * Übernimmt den Element-Fortschritt der gerade laufenden Prüfregel ("12 von 40 Räumen").
   *
   * Anders als die Einheiten ist diese Zahl **regelbezogen**: Sie beginnt bei jeder Prüfregel neu und
   * wird zwischendurch abgeräumt. Eine rein monotone Übernahme wie bei `unitsDone` wäre deshalb
   * falsch – die Zeile bliebe für den Rest des Laufs auf "40 von 40" stehen.
   *
   * Monoton wird nur *innerhalb* derselben Regel gezählt, erkennbar an unverändertem Gesamtwert und
   * Label. Das hält die Zeile ruhig, wenn Poll und SSE sich überholen, und lässt sie beim Wechsel
   * zur nächsten Regel trotzdem sofort umspringen.
   */
  private applyElementProgress(progress: CheckProgress) {
    const total = progress.elementUnitsTotal ?? undefined;
    const label = progress.elementLabel ?? undefined;

    if (!total || total <= 0) {
      // Kein Element-Fan-out (mehr): Zeile ausblenden, statt den letzten Stand einzufrieren.
      if (this.progressInfo.elementUnitsTotal === undefined) return;
      this.progressInfo = {
        ...this.progressInfo,
        elementUnitsTotal: undefined,
        elementUnitsDone: undefined,
        elementLabel: undefined,
      };
      this.updateProgressInfo();
      return;
    }

    const sameRule = this.progressInfo.elementUnitsTotal === total
      && this.progressInfo.elementLabel === label;
    const incomingDone = Math.min(progress.elementUnitsDone ?? 0, total);
    const done = sameRule
      ? Math.max(this.progressInfo.elementUnitsDone ?? 0, incomingDone)
      : incomingDone;

    this.progressInfo = {
      ...this.progressInfo,
      elementUnitsTotal: total,
      elementUnitsDone: done,
      elementLabel: label,
    };
    this.updateProgressInfo();
  }

  private collectLogFromEvent(event: WorkflowExecutionEvent) {
    if (event.logs && Array.isArray(event.logs) && event.logs.length > 0) {
      const newEntries: LogEntry[] = this.mapBackendLogEntries(event.logs);
      this.appendUniqueLogs(newEntries);
    } else if (event.type === 'STEP_COMPLETED' && event.nodeId) {
      // Fallback: poll instance to get logs + duration from StepLogDto
      this.pollInstanceForStepLogs(event.nodeId);
    }
  }

  /**
   * Builds a stable signature for a log entry so the same entry – delivered via
   * both the persisted instance state and replayed SSE events – is only shown once.
   */
  private logSignature(entry: LogEntry): string {
    return [
      entry.occurredAtIso ?? entry.timestamp ?? '',
      this.normalizeLogLevel(entry.level),
      entry.moduleId ?? '',
      entry.phase ?? '',
      entry.message ?? '',
    ].join('|');
  }

  /** Appends log entries to progressLogs, skipping ones already present. */
  private appendUniqueLogs(newEntries: LogEntry[]) {
    if (!newEntries.length) return;
    const seen = new Set(this.progressLogs.map(e => this.logSignature(e)));
    const additions = newEntries.filter(entry => {
      const sig = this.logSignature(entry);
      if (seen.has(sig)) return false;
      seen.add(sig);
      return true;
    });
    if (additions.length) {
      this.progressLogs = this.sortLogsChronologically([...this.progressLogs, ...additions]);
    }
  }

  /**
   * Sortiert das Ereignisprotokoll nach echter Ereigniszeit. Nötig, weil Einträge aus
   * nachgeladenen Sub-Workflows (Hybrid-Regeln, Element-Läufe) erst nach dem Abschluss-Event der
   * Elternprüfung eintreffen, zeitlich aber davor liegen. Sortiert nur, wenn ALLE Einträge eine
   * parsebare ISO-Zeit haben – sonst bleibt die Eintreffreihenfolge erhalten (Live-SSE-Fall).
   */
  private sortLogsChronologically(entries: LogEntry[]): LogEntry[] {
    const keyed = entries.map(entry => ({
      entry,
      time: entry.occurredAtIso ? new Date(entry.occurredAtIso).getTime() : NaN,
    }));
    if (keyed.some(k => Number.isNaN(k.time))) return entries;
    return keyed.sort((a, b) => a.time - b.time).map(k => k.entry);
  }

  /**
   * Polls the instance to retrieve step logs when SSE events don't include them.
   * Updates progressSteps with duration and adds a log entry from statusMessage.
   */
  private pollInstanceForStepLogs(completedNodeId: string) {
    if (!this.instanceId) return;

    this.myChecksService.getInstance(this.instanceId).subscribe({
      next: (instance) => {
        if (!instance.steps) return;

        const stepLog = instance.steps.find(s => s.nodeId === completedNodeId);
        if (!stepLog) return;

        // Update duration from startedAt/completedAt if not already set
        if (stepLog.startedAt && stepLog.completedAt) {
          const start = new Date(stepLog.startedAt).getTime();
          const end = new Date(stepLog.completedAt).getTime();
          const durationSeconds = Math.round((end - start) / 1000);

          // Only update helperText if it's meaningful (not a "waiting" message)
          const isUsefulMessage = stepLog.statusMessage
            && !stepLog.statusMessage.includes('Warte auf')
            && !stepLog.statusMessage.includes('AWAITING');

          this.progressSteps = this.progressSteps.map(s =>
            s.nodeId === completedNodeId && !s.durationSeconds
              ? {
                ...s,
                durationSeconds,
                helperText: isUsefulMessage ? stepLog.statusMessage : s.helperText,
              }
              : s
          );
        }

        const stepOutputLogs = this.mapBackendLogEntries(stepLog?.outputData?.logs);
        if (stepOutputLogs.length > 0) {
          this.appendUniqueLogs(stepOutputLogs);
        } else {
          // Fallback only when no structured logs are available for this step.
          if (stepLog.statusMessage && !stepLog.statusMessage.includes('Warte auf')) {
            const timestamp = stepLog.completedAt
              ? new Date(stepLog.completedAt).toLocaleTimeString('de-DE', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })
              : new Date().toLocaleTimeString('de-DE', {hour: '2-digit', minute: '2-digit', second: '2-digit'});

            const logEntry: LogEntry = {
              timestamp,
              level: 'INFO',
              message: this.sanitizeLogText(stepLog.statusMessage) as string,
              occurredAtIso: this.toIsoString(stepLog.completedAt),
            };
            this.appendUniqueLogs([logEntry]);
          }

          if (stepLog.errorMessage) {
            const logEntry: LogEntry = {
              timestamp: new Date().toLocaleTimeString('de-DE', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              }),
              level: 'ERROR',
              message: this.sanitizeLogText(stepLog.errorMessage) as string,
              occurredAtIso: this.toIsoString(stepLog.completedAt) ?? new Date().toISOString(),
            };
            this.appendUniqueLogs([logEntry]);
          }
        }
      },
    });
  }

  private resetExecutionState(): void {
    this.currentView = 'init';
    this.activeInteractionComponent = null;
    this.activeInteractionInputs = {};
    this.currentPrompt = undefined;
    this.currentStepInfo = undefined;
    this.outputData = undefined;
    this.errorMessage = '';
    this.errorDetails = '';
    this.loadingMessage = 'Prüfung wird gestartet...';
    this.stepperSteps = [];
    this.currentStepIndex = 0;
    this.completedSteps = [];
    this.currentModuleId = '';
    this.progressSteps = [];
    this.progressLogs = [];
    this.progressInfo = {currentStep: 0, totalSteps: 0, elapsedSeconds: 0, percentage: 0, checkName: ''};
    this.progressFloor = 0;
    this.unitStepNodeId = undefined;
    this.userInputs = {};
    this.selectedRuleIds = [];
    this.selectedFileIds = [];
    this.checkResultId = undefined;
    this.executionSteps = [];
    this.workflowSlug = undefined;
    this.checkVersion = undefined;
    this.definitionCheckType = undefined;
    this.selectedRulesData = [];
    this.selectedFilesData = [];
    this.checkDurations = null;
    this.activeLogDetail = null;
    this.reportId = undefined;
    this.rulesLoadedFromInstance = false;
    this.selectionPlan = null;
    this.pendingRuleData = undefined;
    this.pendingFileData = undefined;
    this.pendingTriggerData = undefined;
    this.reportDisplayConfig = null;
    this.instanceId = undefined;
    this.executionStartTime = undefined;
    this.checkName = '';
    this.savingInProgress = false;
  }

  /** Syncs progress steps from instance step logs (used on SSE reconnect or fallback). */
  private syncProgressFromInstance(instance: any) {
    if (!instance.steps || !Array.isArray(instance.steps)) return;
    this.applyInstanceMetadata(instance);
    this.loadSelectedFilesFromInstance(instance);

    for (const stepLog of instance.steps) {
      if (!stepLog.nodeId) continue;

      const status = stepLog.status === 'COMPLETED' ? 'completed' as const
        : stepLog.status === 'RUNNING' ? 'running' as const
          : 'pending' as const;

      let durationSeconds: number | undefined;
      if (stepLog.startedAt && stepLog.completedAt) {
        const start = new Date(stepLog.startedAt).getTime();
        const end = new Date(stepLog.completedAt).getTime();
        durationSeconds = Math.round((end - start) / 1000);
      }

      this.progressSteps = this.progressSteps.map(s =>
        s.nodeId === stepLog.nodeId ? {
          ...s,
          status,
          durationSeconds: durationSeconds ?? s.durationSeconds,
          helperText: stepLog.statusMessage || s.helperText,
        } : s
      );

    }

    // Recalculate progress info (gleiche, monotone Formel wie bei den SSE-Events)
    this.updateProgressInfo();
  }

  /**
   * Resumes an existing workflow instance (e.g., on page reload).
   * Loads the instance state and reconnects to SSE events.
   */
  private resumeExistingInstance(instanceId: string) {
    this.currentView = 'loading';
    this.loadingMessage = 'Prüfung wird fortgesetzt...';

    this.myChecksService.getInstance(instanceId).subscribe({
      next: (instance) => {
        // Prefer the rules this instance ACTUALLY executed (from its state) over the definition
        // manifest's builder-time pre-selection. Otherwise the log view shows stale/other rulesets
        // (e.g. leftovers in the manifest) instead of what really ran — especially for partial
        // rule selections, where the manifest is not the source of truth.
        this.rulesLoadedFromInstance = this.loadSelectedRulesFromInstance(instance);
        if (this.isLogRoute) {
          this.resolveReportIdForInstance(instanceId);
        }
        // Load definition for stepper/manifest if we have one
        if (instance.definitionId) {
          this.definitionId = instance.definitionId;
          this.flowService.getDefinition(instance.definitionId).subscribe({
            next: (def) => {
              this.checkName = def.name ?? '';
              this.workflowSlug = def.slug;
              this.checkVersion = def.version;
              this.definitionCheckType = this.deriveCheckType(def.slug, (def as any).definitionType, def.manifestJson);
              this.loadSelectedRulesFromManifest(def.manifestJson);
              this.loadReportDisplayConfigFromManifest(def.manifestJson);
              this.buildStepperFromManifest(def.manifestJson);
              this.initProgressTracking();
              // Sync state from instance steps
              this.syncProgressFromInstance(instance);
              this.loadLogsFor(instance);
              this.handleInstanceStatus(instance);
            },
            error: () => {
              this.initProgressTracking();
              this.syncProgressFromInstance(instance);
              this.loadLogsFor(instance);
              this.handleInstanceStatus(instance);
            },
          });
        } else {
          this.initProgressTracking();
          this.syncProgressFromInstance(instance);
          this.loadLogsFor(instance);
          this.handleInstanceStatus(instance);
        }
      },
      error: (err) => {
        this.currentView = 'failed';
        this.errorMessage = this.buildInstanceLoadErrorMessage(err, instanceId);
      },
    });
  }

  /**
   * @param attemptSseReconnect Nur true, wenn dieser Poll ausgelöst wurde, weil die SSE-Verbindung
   * selbst fehlgeschlagen ist (siehe subscribeToEvents()-Error-Handler). Der reguläre periodische
   * Sicherheitsnetz-Poll (aus startPolling()) läuft parallel zu einer intakten SSE-Verbindung und
   * darf diese NICHT bei jedem Tick neu aufbauen - sonst entsteht alle ~7s eine neue, nie
   * geschlossene SSE-Subscription (siehe subscribeToEvents(), das this.subscription ohne
   * unsubscribe() überschreibt) und das Verbindungslimit des Browsers wird erschöpft.
   */
  private pollInstanceStatus(attemptSseReconnect = false) {
    // Läuft bereits ein Poll-Request? Dann diesen Tick überspringen, statt eine weitere Anfrage
    // zu starten. Ohne diese Sperre stapeln sich bei einer langsamen Backend-Antwort (z. B.
    // während einer rechenintensiven Prüfung) beliebig viele parallele Requests auf denselben
    // Endpoint auf – das kann das Verbindungslimit des Browsers erschöpfen und die gesamte
    // Seite blockieren ("alle Requests hängen"), bis neu geladen wird.
    if (!this.instanceId || this.pollInFlight) return;
    this.pollInFlight = true;
    this.myChecksService.getInstance(this.instanceId).subscribe({
      next: (instance) => {
        this.pollInFlight = false;
        // Restore progress steps from instance step logs
        this.syncProgressFromInstance(instance);
        // In der Log-Ansicht kommen Protokoll und Prüfdauern aus den eigenen Endpunkten – die
        // Instanz-Response trägt sie nicht mehr (und soll das auch nicht).
        if (this.isLogRoute) {
          this.loadRunLog();
          this.loadDurations();
        }

        if (instance.status === 'COMPLETED') {
          this.stopPolling();
          this.stopElapsedTimer();
          this.currentView = 'loading';
          this.loadingMessage = this.isLogRoute ? 'Ausführungsprotokoll' : 'Bericht wird geöffnet...';
          this.applyCheckTypeFromOutputData(instance.outputData);
          if (!this.isLogRoute) {
            this.outputData = instance.outputData;
            this.currentStepIndex = this.stepperSteps.length - 1;
            this.completedSteps = this.stepperSteps.map((_, i) => i);
          }
          this.progressInfo = {
            ...this.progressInfo,
            status: 'COMPLETED',
            currentStep: this.progressInfo.totalSteps,
            percentage: 100,
            endedAtIso: this.toIsoString(instance.completedAt),
            endedAt: this.toDisplayTime(instance.completedAt),
          };
          // Die Log-Ansicht ist rein lesend – sie darf kein (doppeltes) CheckResult persistieren.
          if (!this.isLogRoute) {
            // Poll gewinnt das Rennen gegen SSE: Ergebnis persistieren und wie im SSE-Pfad
            // sofort auf die geteilte Berichtsseite umleiten, statt auf einer nicht gerenderten
            // 'completed'-Ansicht hängen zu bleiben (siehe navigateToReport()).
            this.saveCheckResult(instance.outputData);
            this.navigateToReport();
          }
        } else if (instance.status === 'FAILED') {
          this.stopElapsedTimer();
          this.currentView = 'loading';
          this.loadingMessage = 'Ausführungsprotokoll';
          this.errorMessage = instance.errorInfo || 'Prüfung fehlgeschlagen';
          this.errorDetails = this.buildErrorDetailsFromInstance(instance);
          this.progressInfo = {
            ...this.progressInfo,
            status: 'FAILED',
            endedAtIso: this.toIsoString(instance.completedAt),
            endedAt: this.toDisplayTime(instance.completedAt),
          };
          // Fehlschlag über Polling erkannt → Credits erstatten (idempotent).
          this.refundCreditsForFailedRun();
          // Direkt ins Ausführungsprotokoll wechseln statt einer Fehlerseite.
          this.navigateToLog();
        } else if (attemptSseReconnect) {
          setTimeout(() => this.subscribeToEvents(), 2000);
        }
      },
      error: () => {
        this.pollInFlight = false;
      },
    });
  }

  /**
   * Woher das Protokoll kommt – die einzige Stelle, die zwischen den beiden Wegen unterscheidet.
   *
   * In der Log-Ansicht über die schlanken Endpunkte `/log` und `/durations`: Diese Ansicht wird auf
   * einem meist abgeschlossenen Lauf geöffnet und braucht den ganzen Baum, also auch die Schritte der
   * Kind- und Enkel-Instanzen.
   *
   * Während einer laufenden Prüfung (Verarbeitungsansicht) bleibt es bei
   * {@link collectLogsFromInstance}: Dort ist SSE die Quelle, das Protokoll wächst live mit, und die
   * Instanz ist ohnehin schon geladen.
   */
  private loadLogsFor(instance: any) {
    if (this.isLogRoute) {
      this.loadRunLog();
      this.loadDurations();
      return;
    }
    this.collectLogsFromInstance(instance);
  }

  /**
   * Lädt das Ereignisprotokoll des Laufs über den schlanken Endpunkt und ersetzt `progressLogs`.
   *
   * Nur in der Log-Ansicht. Vorher entstand dasselbe Protokoll clientseitig aus
   * `GET /workflows/instances/{id}` plus je Prüfregel einem `/children` – mit `inputData` und
   * `outputData` jedes Schrittes im Gepäck. Bei einem Fan-out über hunderte Datei-Regel-Paare waren
   * das mehrere Megabyte, in denen der Prüfbericht mehrfach steckte.
   *
   * Bewusst ein vollständiges Ersetzen statt `appendUniqueLogs`: Der Endpunkt liefert den kompletten
   * Stand: es gibt nichts zu deduplizieren, und ein Ersetzen kann nicht driften.
   */
  private loadRunLog() {
    if (!this.instanceId || this.runLogInFlight) return;
    this.runLogInFlight = true;
    this.flowService.getInstanceLog(this.instanceId, 0, RUN_LOG_PAGE_SIZE).subscribe({
      next: (page) => {
        this.runLogInFlight = false;
        this.progressLogs = this.mapRunLogEntries(page);
        if (page.hasMore) {
          // Kein stilles Abschneiden: Wer 500 von 1200 Einträgen sieht, soll das wissen.
          this.progressLogs = [
            ...this.progressLogs,
            {
              timestamp: '',
              level: 'INFO',
              message: `Es werden die ersten ${page.entries.length} von ${page.total} Ereignissen angezeigt.`,
            },
          ];
        }
      },
      error: () => {
        this.runLogInFlight = false;
      },
    });
  }

  /**
   * Übersetzt die Protokollzeilen des Backends in `LogEntry`.
   *
   * Der Teillauf-Kontext (`scopes`) liefert das Präfix, das früher die synthetischen
   * "<Regel> gestartet/beendet"-Klammern aus den Kind-Instanzen erzeugt haben – jetzt steht die
   * Zuordnung an der Zeile selbst, statt aus zwei zusätzlichen Requests rekonstruiert zu werden.
   */
  private mapRunLogEntries(page: RunLogPage): LogEntry[] {
    const scopes = page.scopes ?? {};
    return (page.entries ?? []).map(entry => {
      const scope = scopes[entry.instanceId];
      const prefix = scope && (scope.depth ?? 0) > 0 && scope.label ? `${scope.label}: ` : '';
      const moduleLabel = this.getStepLabel(entry.nodeId ?? '', entry.moduleId ?? '');
      const failed = entry.status === 'FAILED';
      const message = entry.errorMessage
        ? `${prefix}${moduleLabel}: ${this.sanitizeLogText(entry.errorMessage)}`
        : entry.statusMessage
          ? `${prefix}${this.sanitizeLogText(entry.statusMessage)}`
          : `${prefix}${moduleLabel} ${failed ? 'fehlgeschlagen' : 'abgeschlossen'}`;
      const occurredAt = entry.completedAt ?? entry.startedAt;
      return {
        timestamp: this.toDisplayTime(occurredAt) ?? '–',
        level: failed ? 'ERROR' as const : 'INFO' as const,
        message,
        occurredAtIso: this.toIsoString(occurredAt),
        moduleId: entry.moduleId,
        stepLogId: entry.id,
        hasDetails: !!entry.hasDetails,
      };
    });
  }

  /** Lädt die aggregierten Prüfdauern. Fehler bleiben still – die Ansicht zeigt dann "–". */
  private loadDurations() {
    if (!this.instanceId) return;
    this.flowService.getInstanceDurations(this.instanceId).subscribe({
      next: (durations) => {
        this.checkDurations = durations;
      },
    });
  }

  /**
   * Baut die Detailzeilen eines Schrittes.
   *
   * Gekappte Blobs kommen vom Backend als Text statt als JSON (abgeschnittenes JSON darf nie durch
   * den Parser laufen) – das wird hier als solches ausgewiesen, statt es wie ein vollständiges
   * Objekt aussehen zu lassen.
   */
  private buildDetailRows(detail: StepLogDetail): LogDetailRow[] {
    const rows: LogDetailRow[] = [];
    const push = (key: string, value: unknown) => {
      if (value === null || value === undefined || value === '') return;
      const text = typeof value === 'string' ? value : JSON.stringify(value);
      rows.push(truncateDetailValue(key, text));
    };

    if (detail.durationMs != null) push('dauer', `${detail.durationMs} ms`);
    if (detail.ruleName) push('regel', detail.ruleName);
    if (detail.fileName) push('datei', detail.fileName);
    if (detail.statusMessage) push('status', detail.statusMessage);
    if (detail.errorMessage) push('fehler', detail.errorMessage);

    if (detail.inputTruncated) {
      push(`input (gekürzt, ${detail.inputChars ?? '?'} Zeichen)`, detail.inputPreview);
    } else {
      push('input', detail.inputData);
    }
    if (detail.outputTruncated) {
      push(`output (gekürzt, ${detail.outputChars ?? '?'} Zeichen)`, detail.outputPreview);
    } else {
      push('output', detail.outputData);
    }
    if (detail.logs?.length) {
      push(detail.logsTruncated ? 'modul-logs (gekürzt)' : 'modul-logs', detail.logs);
    }
    return rows;
  }

  private collectLogsFromInstance(instance: any) {
    if (!instance.steps || !Array.isArray(instance.steps)) return;
    const entries: LogEntry[] = [];
    for (const step of instance.steps) {
      const stepOutputLogs = this.mapBackendLogEntries(step?.outputData?.logs);
      if (stepOutputLogs.length > 0) {
        entries.push(...stepOutputLogs);
      } else {
        if (step.statusMessage && !step.statusMessage.includes('Warte auf')) {
          entries.push({
            timestamp: this.toDisplayTime(step.completedAt ?? step.startedAt) ?? '–',
            level: 'INFO',
            message: this.sanitizeLogText(step.statusMessage) as string,
            occurredAtIso: this.toIsoString(step.completedAt ?? step.startedAt),
          });
        }
        if (step.errorMessage) {
          entries.push({
            timestamp: this.toDisplayTime(step.completedAt ?? step.startedAt) ?? '–',
            level: 'ERROR',
            message: this.sanitizeLogText(step.errorMessage) as string,
            occurredAtIso: this.toIsoString(step.completedAt ?? step.startedAt),
          });
        }
      }
      // Bei IDS-Multi-File: jede fehlgeschlagene Datei einzeln protokollieren, damit das
      // Ereignisprotokoll pro Datei einen ERROR-Eintrag zeigt statt nur einer gesammelten Zeile.
      for (const fileResult of (step?.outputData?.results ?? [])) {
        if (fileResult?.success === false && fileResult?.error) {
          entries.push({
            timestamp: this.toDisplayTime(step.completedAt ?? step.startedAt) ?? '–',
            level: 'ERROR',
            message: this.sanitizeLogText(fileResult.error) as string,
            occurredAtIso: this.toIsoString(step.completedAt ?? step.startedAt),
          });
        }
      }
    }
    // Rebuild from scratch but drop entries that are identical across steps
    // (e.g. a module summary that also appears as a statusMessage).
    this.progressLogs = [];
    // Der Reset löscht auch die nachgeladenen Sub-Workflow-Details (Hybrid-Regeln/Element-Läufe).
    // Ohne diese Freigabe würde die Lade-Sperre ein erneutes Nachladen verhindern und die
    // Modul-Einträge wären nach einem Rebuild (z.B. SSE-Reconnect) dauerhaft weg.
    this.appendUniqueLogs(entries);
  }

  /**
   * Baut aus dem check_execution-Step einer fehlgeschlagenen Instanz eine Datei-für-Datei-
   * Aufschlüsselung ("<Datei>: <Fehler | OK>") für das "Details anzeigen"-Feld der Fehleransicht.
   * Gibt einen leeren String zurück, wenn kein results[]-Block vorhanden ist (z. B. Single-File-
   * oder Nicht-IDS-Läufe), sodass die Details-Sektion dann gar nicht erscheint.
   */
  private buildErrorDetailsFromInstance(instance: any): string {
    if (!Array.isArray(instance?.steps)) return '';
    const step = instance.steps.find((s: any) => Array.isArray(s?.outputData?.results));
    if (!step) return '';
    const lines = (step.outputData.results as any[])
      .filter(entry => entry && typeof entry === 'object')
      .map(entry => {
        const name = entry.file_name || '?';
        const detail = entry.success === false ? (entry.error || 'Fehlgeschlagen') : 'OK';
        return `${name}: ${this.sanitizeLogText(detail)}`;
      });
    return lines.join('\n');
  }

  /** Erster nicht-leerer String aus den Kandidaten (`??` allein reicht nicht, da ein vorhandener
   * leerer String einen später folgenden echten Wert sonst verdecken würde). */
  private firstNonEmpty(...candidates: (string | undefined)[]): string | undefined {
    return candidates.find(candidate => typeof candidate === 'string' && candidate.trim() !== '');
  }

  private applyInstanceMetadata(instance: any) {
    const project = this.stateConfig.getProject();
    const organisation = this.stateConfig.getOrganisation();
    // `checkerName` liegt als eigenes Feld auf der Instanz (WorkflowInstanceDto.checkerName), nicht
    // in outputData – outputData dient hier nur als Fallback für ältere/abweichende Backend-Antworten.
    // `??` reicht nicht: ein leerer String verdeckt sonst einen später folgenden echten Wert.
    const checkerName = this.firstNonEmpty(instance.checkerName, instance.outputData?.checker_name, instance.outputData?.checkerName);
    // Die myCheck-Version stammt aus der Workflow-Definition (WorkflowDefinition.version), nicht aus
    // der Instanz – outputData enthält sie nur bei manchen (KI-)Läufen zusätzlich.
    const myCheckVersion = this.firstNonEmpty(this.checkVersion, instance.outputData?.mycheck_version, instance.outputData?.myCheckVersion);
    this.progressInfo = {
      ...this.progressInfo,
      checkId: instance.id ?? this.progressInfo.checkId,
      startedAtIso: this.toIsoString(instance.startedAt) ?? this.progressInfo.startedAtIso,
      startedAt: this.toDisplayTime(instance.startedAt) || this.progressInfo.startedAt,
      endedAtIso: this.toIsoString(instance.completedAt) ?? this.progressInfo.endedAtIso,
      endedAt: this.toDisplayTime(instance.completedAt) || this.progressInfo.endedAt,
      organisationName: organisation?.name ?? this.progressInfo.organisationName,
      projectName: project?.name ?? this.progressInfo.projectName,
      checkerName: checkerName ?? this.progressInfo.checkerName,
      myCheckVersion: myCheckVersion ?? this.progressInfo.myCheckVersion,
      status: instance.status ?? this.progressInfo.status,
    };
  }

  private loadSelectedFilesFromInstance(instance: any) {
    if (this.selectedFilesData.length > 0) return;

    const candidateLists: unknown[] = [];

    // Durchsuche alle Node-States nach project_files in outputs.
    // State-Struktur: { nodeId: { outputs: { project_files: [...] } } }
    if (instance.state && typeof instance.state === 'object') {
      for (const nodeId of Object.keys(instance.state as Record<string, any>)) {
        const nodeState = (instance.state as any)[nodeId];
        if (Array.isArray(nodeState?.outputs?.project_files)) {
          candidateLists.push(nodeState.outputs.project_files);
        }
      }
    }

    if (Array.isArray(instance.outputData?.project_files)) {
      candidateLists.push(instance.outputData.project_files);
    }

    if (Array.isArray(instance.steps)) {
      for (const step of instance.steps) {
        if (Array.isArray(step?.inputData?.project_files)) {
          candidateLists.push(step.inputData.project_files);
        }
        if (Array.isArray(step?.outputData?.project_files)) {
          candidateLists.push(step.outputData.project_files);
        }
      }
    }

    const firstNonEmpty = candidateLists.find(entry => Array.isArray(entry) && entry.length > 0);
    if (!Array.isArray(firstNonEmpty) || firstNonEmpty.length === 0) {
      return;
    }

    this.selectedFilesData = this.mapToSelectedFiles(firstNonEmpty);
  }

  private mapToSelectedFiles(projectFiles: unknown[]): SelectedFileSummary[] {
    const entries = projectFiles.filter((entry): entry is Record<string, any> => {
      return typeof entry === 'object' && entry !== null;
    });

    const result: SelectedFileSummary[] = [];
    const seen = new Set<string>();

    for (const file of entries) {
      const name = this.firstNonEmptyString(file.name, file.fileName, file.filename, file.id, file.fileId) ?? '';
      const fileType = this.firstNonEmptyString(file.fileType, file.type, file.extension) ?? 'IFC';
      const size = typeof file.size === 'number' ? file.size : undefined;

      const fileId = this.firstNonEmptyString(file.id, file.fileId);
      const key = fileId ?? this.firstNonEmptyString(file.link, name) ?? JSON.stringify(file);
      if (seen.has(key)) continue;
      seen.add(key);

      // fileId ist der Zuordnungsschlüssel der Prüfdauer. Der Dateiname bleibt der Rückfall - bei
      // IDS liefert das Modul ohnehin nur Namen.
      result.push({name, fileType, size, fileId});
    }

    return result;
  }

  private firstNonEmptyString(...values: unknown[]): string | undefined {
    for (const value of values) {
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed.length > 0) return trimmed;
      }
    }
    return undefined;
  }

  private normalizeLogLevel(level?: string): 'INFO' | 'ERROR' | 'WARN' {
    const normalized = (level || '').toUpperCase();
    if (normalized === 'ERROR') return 'ERROR';
    if (normalized === 'WARN' || normalized === 'WARNING') return 'WARN';
    return 'INFO';
  }

  /**
   * Entfernt/maskiert interne Backend-Details (AWS/S3-URLs, ARNs, presigned Links,
   * workflow-instance-IDs etc.), die für Nutzer nicht relevant sind und im Issue #1981
   * als Feedback ("Logs enthalten AWS-URLs und interne IDs") gemeldet wurden.
   */
  private sanitizeLogText(value: unknown): unknown {
    if (typeof value !== 'string') return value;
    return value
      // presigned S3 URLs (z. B. https://bucket.s3.eu-central-1.amazonaws.com/...)
      .replace(/https?:\/\/[^\s"'<>]*\.s3\.[^\s"'<>]*amazonaws\.com\/[^\s"'<>]*/gi, '[S3-URL]')
      .replace(/https?:\/\/[^\s"'<>]*\.amazonaws\.com\/[^\s"'<>]*/gi, '[AWS-URL]')
      // AWS ARNs (z. B. arn:aws:sqs:eu-central-1:123456789012:queue)
      .replace(/arn:aws:[a-z0-9-]+:[a-z0-9-]+:\d{12}:[^\s"'<>]+/gi, '[ARN]')
      // Roh AWS-IDs / presigned-Parameter (?X-Amz-...&Signature=...)
      .replace(/X-Amz-[A-Za-z0-9-]+=[^\s"'&<>]+/gi, '[redacted]')
      .replace(/Signature=[^\s"'&<>]+/gi, '[redacted]')
      .replace(/expires=\d+/gi, '[redacted]')
      // workflow-instance-IDs / GUIDs die als interne IDs durchgereicht werden
      .replace(/\b[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\b/g, '[id]')
      // AWS request ids (24 hex chars)
      .replace(/\b[0-9a-fA-F]{24,}\b/g, '[id]');
  }

  private sanitizeLogDetails(details: unknown): Record<string, unknown> | undefined {
    if (!details || typeof details !== 'object' || Array.isArray(details)) {
      return undefined;
    }
    const sanitized: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(details as Record<string, unknown>)) {
      if (typeof value === 'string') {
        sanitized[key] = this.sanitizeLogText(value);
      } else if (Array.isArray(value)) {
        sanitized[key] = value.map(v => this.sanitizeLogText(v));
      } else if (value && typeof value === 'object') {
        sanitized[key] = this.sanitizeLogDetails(value) ?? value;
      } else {
        sanitized[key] = value;
      }
    }
    return sanitized;
  }

  private normalizeLogDetails(details: unknown): Record<string, unknown> | undefined {
    if (!details || typeof details !== 'object' || Array.isArray(details)) {
      return undefined;
    }
    return this.sanitizeLogDetails(details);
  }

  private mapBackendLogEntries(logs: unknown): LogEntry[] {
    if (!Array.isArray(logs)) return [];
    return logs.map((l: any) => ({
      timestamp: this.toDisplayTime(l?.timestamp) || new Date().toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }),
      level: this.normalizeLogLevel(l?.level),
      message: this.sanitizeLogText(l?.message || '') as string,
      occurredAtIso: l?.timestamp,
      moduleId: l?.moduleId,
      phase: l?.phase,
      details: this.normalizeLogDetails(l?.details ?? l?.context),
    }));
  }

  private toDisplayTime(value?: string | Date): string | undefined {
    if (!value) return undefined;
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return undefined;
    return date.toLocaleTimeString('de-DE', {hour: '2-digit', minute: '2-digit'});
  }

  private toIsoString(value?: string | Date): string | undefined {
    if (!value) return undefined;
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return undefined;
    return date.toISOString();
  }

  /**
   * Ladehinweis aus dem strukturierten Sub-Run-Fortschritt (ein Teillauf je Regelset).
   * Liefert {@code undefined}, wenn das Event keinen Sub-Run trägt – dann greift die
   * modul-basierte Meldung.
   */
  private getSubRunMessage(subRun?: WorkflowSubRunInfo): string | undefined {
    if (!subRun) return undefined;
    const position = subRun.index && subRun.total ? ` (${subRun.index}/${subRun.total})` : '';
    return subRun.rulesetName
      ? `Regelset "${subRun.rulesetName}" wird geprüft...${position}`
      : `myCheck wird geprüft...${position}`;
  }

  /**
   * Überträgt den Sub-Run-Fortschritt auf die Regelset-Zusammenfassung: das laufende Regelset wird
   * als "läuft" markiert, alle davor liegenden als abgeschlossen.
   *
   * Bewusst nur ein Statusfeld je Regelset und keine Neuzuweisung des Arrays an anderer Stelle:
   * `selectedRulesData` wird an mehreren Stellen komplett ersetzt (Auswahl, Instanz-State,
   * Manifest-Fallback), ein Merge über die `rulesetId` bleibt davon unabhängig.
   */
  private applySubRunProgress(subRun?: WorkflowSubRunInfo): void {
    if (!subRun?.rulesetId || this.selectedRulesData.length === 0) return;
    const runningIndex = this.selectedRulesData.findIndex(r => r.rulesetId === subRun.rulesetId);
    if (runningIndex < 0) return;
    this.selectedRulesData = this.selectedRulesData.map((summary, index) => ({
      ...summary,
      runStatus: index < runningIndex ? 'completed' : index === runningIndex ? 'running' : 'pending',
    }));
  }

  /** Markiert alle Regelsets als abgeschlossen bzw. – bei Fehlschlag – das laufende als fehlgeschlagen. */
  private finalizeSubRunProgress(failed: boolean): void {
    if (this.selectedRulesData.length === 0) return;
    if (!this.selectedRulesData.some(r => r.runStatus)) return;
    this.selectedRulesData = this.selectedRulesData.map(summary => ({
      ...summary,
      runStatus: failed
        ? (summary.runStatus === 'running' ? 'failed' : summary.runStatus)
        : 'completed',
    }));
  }

  /**
   * Setzt den Statustext, sobald die Einzelprüfungen durch sind.
   *
   * Vorher blieb dort die letzte Modul- bzw. Sub-Run-Meldung stehen ("Regelset 'X' wird geprüft…"),
   * obwohl längst nichts mehr geprüft, sondern nur noch zusammengeführt wurde. Die Sub-Run-Meldung
   * wird deshalb bewusst überschrieben: Sind alle Einheiten gezählt, sagt sie nichts mehr aus.
   *
   * Der Text für die Berichtsphase kommt dagegen aus {@link getLoadingMessage} über die
   * Aggregator-Module – der Schrittwechsel liefert ihn ohnehin.
   */
  private refreshPhaseMessage() {
    if (this.isLogRoute) return;
    const unitStep = this.unitStepNodeId
      ? this.progressSteps.find(s => s.nodeId === this.unitStepNodeId)
      : undefined;
    if (unitStep?.status !== 'running') return;

    const total = this.progressInfo.unitsTotal;
    if (!total || total <= 0) return;
    if ((this.progressInfo.unitsDone ?? 0) < total) return;

    this.loadingMessage = 'Einzelprüfungen werden aggregiert...';
  }

  private getLoadingMessage(moduleId?: string, nodeId?: string): string {
    // Die Berichtsphase bekommt bewusst ihren eigenen Text und NICHT den Registry-Namen des Moduls:
    // Für die wartende Person ist die Aussage "der Bericht entsteht gerade" der Punkt, nicht wie das
    // Modul heißt, das ihn zusammensetzt.
    if (moduleId === 'ai_result_aggregator' || moduleId === 'hoai_result_aggregator') {
      return 'Bericht wird erstellt...';
    }
    // Try to find a meaningful label from the execution steps
    if (nodeId && this.executionSteps.length > 0) {
      const step = this.executionSteps.find(s => s.nodeId === nodeId);
      if (step && step.label && step.label !== 'Verarbeitung') {
        return `${step.label}...`;
      }
    }
    // Fallback: generic message
    switch (moduleId) {
      case 'check_data_preparation':
        return 'Prüfdaten werden aufbereitet...';
      case 'check_execution':
        return 'Prüfung wird ausgeführt...';
      case 'HYBRID_RULESET_EXECUTOR':
        return 'Hybride Prüfregeln werden ausgeführt...';
      default:
        if (moduleId?.startsWith('HYBRID_RULESET_EXECUTOR:')) {
          // Format: HYBRID_RULESET_EXECUTOR:2/5
          const progress = moduleId.split(':')[1];
          return `Regel ${progress} wird geprüft...`;
        }
        return 'Schritt wird ausgeführt...';
    }
  }

  private buildInstanceLoadErrorMessage(err: unknown, instanceId: string): string {
    const httpError = err as Partial<HttpErrorResponse> | undefined;
    if (httpError?.status === 404) {
      return `Instanz nicht gefunden (${instanceId}). Die Ausführung existiert nicht mehr oder der Link ist veraltet.`;
    }

    const backendMessage =
      (typeof httpError?.error === 'object' && httpError?.error && 'message' in httpError.error)
        ? String((httpError.error as Record<string, unknown>)['message'] ?? '')
        : '';
    const plainError = typeof httpError?.error === 'string' ? httpError.error : '';
    const fallback = backendMessage || plainError || (typeof httpError?.message === 'string' ? httpError.message : '');

    if (fallback) {
      return `Instanz konnte nicht geladen werden: ${fallback}`;
    }
    return `Instanz konnte nicht geladen werden (${instanceId}).`;
  }

  /**
   * Baut die Regel-Zusammenfassung aus dem tatsaechlichen Instanz-State (rule_selection-Outputs) auf –
   * also aus dem, was dieser Lauf wirklich ausgefuehrt hat, nicht aus der Manifest-Vorauswahl.
   * Gibt true zurueck, wenn verwertbare Regel-Outputs gefunden und uebernommen wurden.
   *
   * Vollstaendig gewaehlte IDS-/HYBRID-Sets stehen im State nur als EINE Pseudo-Regel (siehe
   * {@link isWholeRulesetBlock}); fuer die werden die echten Regeln nachgeladen, damit der
   * Ladebildschirm nach einem Reload dieselbe Regelzahl und -liste zeigt wie waehrend der Auswahl.
   */
  private loadSelectedRulesFromInstance(instance: any): boolean {
    const blocks = this.extractRuleSelectionOutputsFromInstanceState(instance);
    if (blocks.length === 0) return false;

    const buildSummary = (outputs: Record<string, unknown>,
                          ruleset: Ruleset | undefined,
                          expandedNames: Map<string, string[]>): SelectedRuleSummary | null => {
      const type = this.firstNonEmptyString(outputs['ruleset_type'], outputs['rule_type']) ?? ruleset?.type ?? 'IDS';
      const ruleNames = this.asStringArray(outputs['rule_names']);
      // rule_ids ist positionsgleich zu rule_names (RuleGroupResolver#addRule füllt beide im
      // Gleichschritt). Nur übernehmen, wenn die Längen das bestätigen - siehe
      // SelectedRuleSummary.ruleIds.
      const ruleIdList = this.asStringArray(outputs['rule_ids']);
      const singleName = this.firstNonEmptyString(outputs['rule_name']);
      const rulesetId = this.firstNonEmptyString(outputs['ruleset_id']);
      const ruleId = this.firstNonEmptyString(outputs['rule_id']);
      // Bei IDS-Teilauswahl wird ruleset_id/rule_id clientseitig entfernt; der Regelset-Titel bleibt
      // aber in der (gefilterten) IDS-XML erhalten und ist die zuverlaessigste Quelle fuer den Header.
      const idsXml = this.firstNonEmptyString(outputs['ids_xml']);
      const idsTitle = idsXml ? /<ids:title>([^<]+)<\/ids:title>/i.exec(idsXml)?.[1]?.trim() : undefined;

      if (ruleNames.length === 0 && !singleName && !rulesetId && !ruleId && !idsTitle) {
        return null;
      }

      // Ganzes Set gewaehlt: die eine Pseudo-Regel durch die echten Regeln ersetzen. Klappt das
      // Nachladen nicht, bleibt wenigstens die Anzahl aus dem Regelset korrekt.
      let names: string[] | undefined = ruleNames.length > 0 ? ruleNames : undefined;
      let ruleCount: number | undefined;
      const wholeRuleset = ruleset && this.isWholeRulesetBlock(outputs);
      if (wholeRuleset) {
        const expanded = expandedNames.get(ruleset!.id);
        if (expanded && expanded.length > 0) {
          names = expanded;
        } else if (ruleset!.ruleCount > ruleNames.length) {
          names = undefined;
          ruleCount = ruleset!.ruleCount;
        }
      }
      const namesFromState = names === undefined ? false : names === ruleNames;

      const header = ruleset?.name ?? idsTitle ?? (ruleNames.length > 1 ? (this.checkName || undefined) : undefined)
        ?? singleName ?? rulesetId ?? ruleId ?? 'Ausgewählte Regeln';
      return {
        name: header,
        type,
        ruleNames: names,
        ruleCount,
        // Nur die IDs aus dem State sind positionsgleich zu den Namen aus dem State. Die
        // nachgeladenen IDS-Spezifikations-IDs sind synthetisch ("<rulesetId>::<specName>") und
        // wuerden die Prüfdauer-Zuordnung fehlleiten - deshalb hier bewusst keine IDs.
        ruleIds: namesFromState && ruleIdList.length === ruleNames.length && ruleNames.length > 0
          ? ruleIdList
          : undefined,
        // Zuordnungsschlüssel für den Sub-Run-Fortschritt (WorkflowEvent.subRun.rulesetId).
        rulesetId,
      };
    };

    const applyAll = (rulesetsById: Map<string, Ruleset>, expandedNames: Map<string, string[]>) => {
      this.selectedRulesData = blocks
        .map(outputs => {
          const rulesetId = this.firstNonEmptyString(outputs['ruleset_id']);
          return buildSummary(outputs, rulesetId ? rulesetsById.get(rulesetId) : undefined, expandedNames);
        })
        .filter((summary): summary is SelectedRuleSummary => summary !== null);
    };

    const hasRulesetId = blocks.some(outputs => this.firstNonEmptyString(outputs['ruleset_id']));
    if (!hasRulesetId) {
      applyAll(new Map(), new Map());
      return true;
    }

    this.rulesetService.listRulesets(this.projectId).subscribe({
      next: (result) => {
        const rulesetsById = new Map([...result.prebuilt, ...result.custom].map(rs => [rs.id, rs]));
        // Regelsets, deren Einzelregeln fehlen, weil der State sie zu einer Pseudo-Regel
        // zusammengefasst hat.
        const toExpand = new Map<string, Ruleset>();
        for (const outputs of blocks) {
          if (!this.isWholeRulesetBlock(outputs)) continue;
          const rulesetId = this.firstNonEmptyString(outputs['ruleset_id'])!;
          const ruleset = rulesetsById.get(rulesetId);
          if (ruleset && ruleset.ruleCount > 1) toExpand.set(rulesetId, ruleset);
        }
        this.loadRuleNamesFor([...toExpand.values()]).subscribe(expandedNames => {
          applyAll(rulesetsById, expandedNames);
        });
      },
      error: () => applyAll(new Map(), new Map()),
    });
    return true;
  }

  /**
   * Laedt die Einzelregelnamen der uebergebenen Regelsets in EINEM Rutsch (Regelset-ID → Namen).
   *
   * Bewusst gebuendelt, damit `selectedRulesData` genau einmal gesetzt wird und der
   * Ladebildschirm nicht zwischen Teilzustaenden flackert. `getRulesetRules` faengt Fehler selbst
   * ab und liefert dann eine leere Liste - der Aufrufer faellt in dem Fall auf `ruleCount` zurueck.
   */
  private loadRuleNamesFor(rulesets: Ruleset[]): Observable<Map<string, string[]>> {
    if (rulesets.length === 0) return of(new Map<string, string[]>());
    return forkJoin(rulesets.map(rs => this.rulesetService.getRulesetRules(rs, this.projectId))).pipe(
      map(rulesPerSet => new Map(rulesPerSet.map(
        (rules, index) => [rulesets[index].id, rules.map(rule => rule.name)] as [string, string[]]))),
      catchError(() => of(new Map<string, string[]>())),
    );
  }

  /**
   * Beschreibt der Output-Block das *ganze* Regelset statt einzelner Regeln?
   *
   * Erkennungsmerkmal ist, dass die Regel-ID die Regelset-ID selbst ist: so legen
   * `RuleGroupResolver#addRule` (Backend) und `collectRuleGroups` (Auswahlmaske) ein vollstaendig
   * gewaehltes IDS-/HYBRID-Set ab, weil dessen Spezifikationen keine im Backend aufloesbare ID
   * haben. Eine Teilauswahl traegt dagegen die synthetischen IDs "<rulesetId>::<specName>" bzw.
   * echte KI-Regel-IDs - dort stehen die richtigen Namen bereits im State.
   */
  private isWholeRulesetBlock(outputs: Record<string, unknown>): boolean {
    const rulesetId = this.firstNonEmptyString(outputs['ruleset_id']);
    if (!rulesetId) return false;
    return this.asStringArray(outputs['rule_ids']).includes(rulesetId)
      || this.firstNonEmptyString(outputs['rule_id']) === rulesetId;
  }

  /**
   * Sucht im Instanz-State die Bloecke der Regelauswahl. Gibt ein leeres Array zurueck, wenn der
   * State keine Regelauswahl enthaelt. Liefert mehrere Bloecke, falls der Lauf Regeln aus mehreren
   * Regelsets ausgefuehrt hat, statt sie auf den ersten Treffer zu reduzieren.
   *
   * <p>Entscheidend ist die *Form* des Blocks, nicht die Node-ID: die Nodes heissen in allen
   * Definitionen `step_1`, `step_2`, … Nur der `rule_selection`-Step schreibt `rule_groups`;
   * spaetere Module echoen dagegen einzelne Regelfelder (`check_data_preparation` → `rule_id`,
   * `check_execution` → `rule_name`). Wuerden die als Auswahl durchgehen, entstuenden daraus nach
   * einem abgeschlossenen Lauf zusaetzliche Pseudo-Regelsets.
   */
  private extractRuleSelectionOutputsFromInstanceState(instance: any): Array<Record<string, unknown>> {
    const state = instance?.state;
    if (!state || typeof state !== 'object') return [];

    const hasRuleKeys = (obj: any): boolean =>
      obj && typeof obj === 'object'
      && (obj['rule_id'] != null || obj['rule_ids'] != null || obj['rule_name'] != null
        || obj['rule_names'] != null || obj['ruleset_id'] != null || obj['ids_xml'] != null
        || Array.isArray(obj['rule_groups']));

    /**
     * Ein Output-Block mit `rule_groups` (gemischter Lauf) wird in einen Block je Regelset
     * aufgefaltet – die flachen Felder daneben beschreiben nur die erste Gruppe und würden die
     * Zusammenfassung auf ein Regelset reduzieren.
     */
    const expandGroups = (outputs: Record<string, unknown>): Array<Record<string, unknown>> => {
      const groups = outputs['rule_groups'];
      if (!Array.isArray(groups) || groups.length === 0) return [outputs];
      return groups
        .filter((group): group is Record<string, unknown> => !!group && typeof group === 'object')
        .map(group => ({
          ruleset_id: group['ruleset_id'],
          ruleset_type: group['ruleset_type'],
          rule_type: group['ruleset_type'],
          rule_ids: group['rule_ids'],
          rule_names: group['rule_names'],
          rule_name: Array.isArray(group['rule_names']) ? group['rule_names'][0] : undefined,
          ids_xml: group['ids_xml'],
        }));
    };

    const collect = (nodeIds: string[],
                     accept: (outputs: Record<string, unknown>) => boolean): Array<Record<string, unknown>> => {
      const blocks: Array<Record<string, unknown>> = [];
      for (const nodeId of nodeIds) {
        const outputs = state[nodeId]?.outputs;
        if (hasRuleKeys(outputs) && accept(outputs as Record<string, unknown>)) {
          blocks.push(...expandGroups(outputs as Record<string, unknown>));
        }
      }
      return blocks;
    };

    const stepNodeIds = Object.keys(state).filter(nodeId => nodeId !== 'trigger_0');
    const hasGroups = (outputs: Record<string, unknown>) => Array.isArray(outputs['rule_groups']);
    const hasRuleLists = (outputs: Record<string, unknown>) =>
      Array.isArray(outputs['rule_names']) || Array.isArray(outputs['rule_ids']);
    const hasRulesetId = (outputs: Record<string, unknown>) =>
      this.firstNonEmptyString(outputs['ruleset_id']) !== undefined;

    /** Leere Trefferliste = dieser Tier greift nicht, der naechste ist dran. */
    const firstNonEmpty = (found: Array<Record<string, unknown>>) => found.length > 0 ? found : undefined;

    const blocks =
      // 1) Der Regelauswahl-Step: als einziger schreibt er die gruppierte Auswahl.
      firstNonEmpty(collect(stepNodeIds, hasGroups))
      // 2) Frontend-Vorbefuellung im Trigger (manuelle Auswahl ohne interaktiven Step).
      ?? firstNonEmpty(collect(['trigger_0'], hasGroups))
      // 3) Alt-Manifeste ohne rule_groups: flache Listenfelder.
      ?? firstNonEmpty(collect(stepNodeIds, hasRuleLists))
      ?? firstNonEmpty(collect(['trigger_0'], () => true))
      // 4) Letzte Rettung: Einzelregel-Auswahl. `ruleset_id` grenzt sie gegen die Modul-Echos ab.
      ?? firstNonEmpty(collect(stepNodeIds, hasRulesetId))
      ?? [];

    // Ein Regelset darf nur einmal erscheinen, auch wenn mehrere Steps es fuehren.
    const seen = new Set<string>();
    return blocks.filter(outputs => {
      const rulesetId = this.firstNonEmptyString(outputs['ruleset_id']);
      if (!rulesetId) return true;
      if (seen.has(rulesetId)) return false;
      seen.add(rulesetId);
      return true;
    });
  }

  /** Baut die Regel-Zusammenfassung fuer den Ladebildschirm aus gespeicherten Manifest-Einstellungen auf. */
  private loadSelectedRulesFromManifest(manifestJson?: string) {
    // Der Instanz-State (was wirklich lief) hat Vorrang vor der Manifest-Vorauswahl.
    if (this.rulesLoadedFromInstance) return;
    const selection = this.extractRuleSelectionFromManifest(manifestJson);
    if (!selection) {
      this.selectedRulesData = [];
      return;
    }

    const selectedRuleSetIds = this.asStringArray(selection.selectedRuleSetIds);
    const selectedRuleIds = this.asStringArray(selection.selectedRuleIds);
    if (selectedRuleSetIds.length === 0 && selectedRuleIds.length === 0) {
      this.selectedRulesData = [];
      return;
    }

    this.rulesetService.listRulesets(this.projectId).subscribe({
      next: (result) => {
        const allRulesets = [...result.prebuilt, ...result.custom];
        const byId = new Map(allRulesets.map(ruleset => [ruleset.id, ruleset]));
        // Die Manifest-Auswahl kennt nur Regelset-IDs. Die Einzelregeln dazu nachladen, sonst
        // zaehlt der Ladebildschirm jedes Set als eine Regel.
        const ids = [...new Set([...selectedRuleSetIds, ...selectedRuleIds])];
        const toExpand = ids
          .map(id => byId.get(id))
          .filter((ruleset): ruleset is Ruleset => !!ruleset && ruleset.ruleCount > 1);

        this.loadRuleNamesFor(toExpand).subscribe(namesById => {
          const summaries: SelectedRuleSummary[] = [];
          const seen = new Set<string>();

          const push = (id: string, isRuleSetId: boolean) => {
            if (seen.has(id)) return;
            seen.add(id);
            const ruleset = byId.get(id);
            const ruleNames = namesById.get(id);
            summaries.push({
              name: ruleset?.name ?? id,
              type: ruleset?.type ?? selection.selectedRulesetType ?? 'IDS',
              ruleNames: ruleNames?.length ? ruleNames : undefined,
              // Ohne Namen wenigstens die Anzahl - siehe SelectedRuleSummary.ruleCount.
              ruleCount: ruleNames?.length ? undefined : ruleset?.ruleCount,
              // Zuordnungsschlüssel für den Sub-Run-Fortschritt pro Regelset.
              // Einzelregeln: bei IDS ist die Regel-ID zugleich die Regelset-ID.
              rulesetId: isRuleSetId ? id : (ruleset ? id : undefined),
            });
          };

          selectedRuleSetIds.forEach(id => push(id, true));
          selectedRuleIds.forEach(id => push(id, false));
          this.selectedRulesData = summaries;
        });
      },
      error: () => {
        const fallbackType = selection.selectedRulesetType ?? 'IDS';
        this.selectedRulesData = [
          ...selectedRuleSetIds.map(id => ({name: id, type: fallbackType})),
          ...selectedRuleIds.map(id => ({name: id, type: fallbackType})),
        ];
      },
    });
  }

  private loadReportDisplayConfigFromManifest(manifestJson?: string) {
    this.reportDisplayConfig = resolveReportDisplayConfig(manifestJson, {
      checkType: this.definitionCheckType,
    });
  }

  private extractRuleSelectionFromManifest(manifestJson?: string): {
    selectedRuleSetIds: unknown;
    selectedRuleIds: unknown;
    selectedRulesetType?: string;
  } | null {
    return extractReportSelectionFromManifest(manifestJson);
  }

  private asStringArray(value: unknown): string[] {
    return Array.isArray(value)
      ? value.filter((entry): entry is string => typeof entry === 'string')
      : [];
  }

  /** Wandelt einen Manifest-Wert in eine positive Zahl um; gibt undefined zurück, wenn nicht sinnvoll. */
  private asNumber(value: unknown): number | undefined {
    const num = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(num) && num > 0 ? num : undefined;
  }

  private startPolling() {
    this.stopPolling();
    this.pollTimer = setInterval(
      () => this.pollInstanceStatus(),
      MyChecksExecutionComponent.INSTANCE_POLL_INTERVAL_MS,
    );
    this.startProgressPolling();
  }

  private stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = undefined;
    }
    this.pollInFlight = false;
    this.stopProgressPolling();
  }

  /**
   * Pollt den schlanken Fortschritts-Endpunkt: die echten Einzelprüfungen und der Endstatus.
   *
   * Dies ist die verbindliche Fortschrittsquelle. Das `PROGRESS`-SSE-Event verkürzt nur die Latenz
   * zwischen zwei Ticks und kann ausfallen, weil die Emitter im Backend pro Replika im Speicher
   * liegen – bei mehreren Replikas hängt dieser Stream regelmäßig nicht an der Replika, die die
   * Modul-Responses des Laufs verarbeitet.
   */
  private startProgressPolling() {
    if (this.isLogRoute) return;
    this.stopProgressPolling();
    this.progressPollTimer = setInterval(
      () => this.pollProgress(),
      MyChecksExecutionComponent.PROGRESS_POLL_INTERVAL_MS,
    );
  }

  private stopProgressPolling() {
    if (this.progressPollTimer) {
      clearInterval(this.progressPollTimer);
      this.progressPollTimer = undefined;
    }
    this.progressPollInFlight = false;
  }

  /**
   * Ein Tick des Fortschritts-Polls. Erkennt zusätzlich den Endstatus und löst dann **einen**
   * vollständigen Instanz-Abruf aus, der Bericht/Fehlerfall wie bisher behandelt – dadurch reagiert
   * der Abschluss weiterhin innerhalb von Sekunden, ohne dass dafür im Sekundentakt die komplette
   * Instanz übertragen werden muss.
   */
  private pollProgress() {
    if (!this.instanceId || this.progressPollInFlight) return;
    this.progressPollInFlight = true;
    this.flowService.getInstanceProgress(this.instanceId).subscribe({
      next: (progress) => {
        this.progressPollInFlight = false;
        this.applyUnitProgress(progress);
        if (progress.status === 'COMPLETED' || progress.status === 'FAILED') {
          this.stopProgressPolling();
          this.pollInstanceStatus();
        }
      },
      error: () => {
        this.progressPollInFlight = false;
      },
    });
  }
}
